/**
 * W1-15 (timeline-host): `createTimelineHost` (contract `CreateTimelineHost`, render/types.ts; ARCHITECTURE.md §7.7,
 * DESIGN.md §4.5-4.6, §8.2).
 *
 * One host per Chart workspace. It owns the shared vertical time axis (time flows up), lays out the column
 * instances contributed by the modules' `TimelineColumnDef`s plus its built-in columns (the time gutter, pinned at
 * the left edge, and the waveform column while a waveform source is set), places the overview strip at the right
 * edge, and routes every pointer and wheel gesture.
 *
 * Drawing (W3-03 scroll cache, UX-027 / UX-030): every column shows ONE opaque canvas (the surface, scaled by the
 * device pixel ratio), so the compositor blends a single layer per column. Its static and content layers are drawn
 * into two hidden strips (static, then base = static + content) and copied onto the surface; the view's fixed parts,
 * its overlay and the host's overlay are drawn over that copy with the live axis on every compose. For a view that
 * opts in (`ScrollCacheView.scrollCache`), a strip spans more than the visible range in the direction the view moves
 * (following the playhead, a recent scroll), so following and scrolling only copy other rows of the cached strip;
 * other views get strips of exactly the visible range. Strips are redrawn whole (no dirty ranges, so stale content
 * cannot exist) when
 *   - they no longer cover the visible range 1:1 (zoom, resize, pixel ratio, scrolled out of the strip; while a
 *     wheel / pinch zoom runs, the old strips are shown scaled and redrawn once it settles);
 *   - a ChangeSet arrived (`applyChangeSet`, or `invalidate(undefined, 'content')` from the panel) or the engine
 *     revision changed (`setRevisionSource`): the content of the columns the change can reach (UX-029: a
 *     line-scoped change asks each view's `DocScopeView.contentAffected`, with the change's ops and the strip's
 *     beat range, so off-screen note edits and other lines' key edits skip columns entirely; global scopes and
 *     unexplained engine revisions redraw every content layer; tempo/timing changes also static), wherever
 *     in the song the change happened;
 * and the surface is composed again when the axis moved, a strip was redrawn, the playhead moved, a marquee / range
 * drag is live, or a view asked (`invalidate(id, 'overlay')`). Frames come from the app's FrameScheduler ('timeline'
 * priority); the host only requests frames when something is dirty, and every frame while playing (the transport
 * keeps the scheduler continuous).
 *
 * Gestures (DESIGN.md §8.1-8.2):
 *   - wheel: normalised deltaY scrolls time (settings.editing.scrollSensitivity beats per 100 px; down = later),
 *     Shift = fast (x4; not on macOS, where Shift+wheel is a horizontal swipe), Ctrl / Cmd / trackpad pinch = zoom
 *     time at the cursor, Alt = cycle the beat snap (default preset) or fast scroll (RPE preset), Ctrl+Shift =
 *     horizontal zoom of the column under the cursor (lane X zoom, value zoom), horizontal wheel = horizontal pan of
 *     that column. A column's own `onWheel` gets the event first. In the RPE preset a mouse Ctrl+wheel is reported
 *     as a 'wheel' event (line switching, fine-tuning belong to other packages) while a pinch still zooms.
 *   - middle drag pans time (and the column's horizontal axis);
 *   - follow: while playing with followPlayhead on, the playhead stays at playheadRatio of the height; a manual
 *     scroll pauses following until playback restarts, a seek from the gutter or the overview, or `resumeFollow()`.
 *     While paused, `setPlayhead` never moves the view: after a keyboard seek (Home/End, PageUp/PageDown) the panel
 *     calls `reveal(beat)` (scrolls only when the beat is off screen);
 *   - pointer routing: a press goes to the column under it (`hitTest` then `onPointerDown`); moves and the release go
 *     to the same column (implicit capture, coordinates relative to it, auto-scroll near the top and bottom edges,
 *     and for a marquee also near the left and right edges of the columns area when the columns overflow);
 *     hover moves go to the column under the pointer (`onPointerLeave` when it leaves, TimelineColumnView).
 *   - marquee across columns: a view calls `host.capturePointer(itsInstanceId)` in `onPointerDown` (typically on an
 *     empty hit with the select tool), or presses on empty space of a column that has no `onPointerDown`. The host
 *     then draws the rubber band over every column it spans, asks each column's `itemsInRect` and merges the
 *     answers into one 'marquee' event (Shift add, Ctrl/Cmd toggle, Alt or Ctrl+Shift subtract, else replace);
 *     Esc cancels. A click without a drag reports an empty 'replace' marquee (clears the selection).
 *   - gutter: drag = time-range selection (a 'timeRange' event, plus a gutter-owned 'marquee' with the notes and
 *     keys of every column inside the range, whatever their X, DESIGN.md §5.4), Alt+drag = loop, dragging the
 *     playhead marker = scrub ('seek' start/move/end; the view holds still meanwhile; Alt held = no snap),
 *     click = seek, right-click = 'gutterContextMenu' (gutterColumn.ts); overview strip: click/drag seeks (Esc puts
 *     the playhead and the view back), Alt+drag = loop (overviewStrip.ts). In the Alt-free variant Ctrl+Shift+drag
 *     sets the loop as well, and Ctrl+Shift held during a scrub is the no-snap modifier.
 *   - horizontal scrolling: the columns area scrolls sideways (scrollbar, horizontal wheel over a column without
 *     `panX` or over empty space, middle drag over such a column, vertical wheel over the scrollbar). Whenever the
 *     columns start or stop overflowing, the host re-reads the scrollbar thickness, so the canvases end above a
 *     classic scrollbar and presses on it are never routed to a column.
 *   - view sync: `onViewChange` reports the host's own changes; `setView` applies the axis fields (zoom, bottom,
 *     snap) only as new intents: a config equal to one the host reported recently and has since superseded is a late
 *     echo of the panel's store mirror (ignored), and a field equal to what the panel last saw (its previous setView
 *     or the host's latest report, whichever came later) is stale (ignored), so asynchronous echoes and stale fields
 *     cannot pull the view back while scrolling, while a value asked for again after the host reported moving away
 *     from it applies.
 *
 * The contract stays the public surface; everything else is additive (`TimelineHostController`): options
 * (translate, sensitivity, preset, platform), events (`on`), playback state, ChangeSets, the engine revision, the
 * waveform and overview data, the time range, layout queries and header slots (the panel portals each column's
 * React header into `headerSlot(id)`; render code never imports React at runtime). See
 * docs/design/change-requests/W1-15.md.
 *
 * Column views handed to the host are owned by it: a view whose instance leaves the layout (or is replaced by a new
 * view object under the same id) is disposed, and every view is disposed with the host. Column defs must create a
 * fresh view when an instance (re)appears.
 */

import type { ComponentType } from 'react';
import { createMeterMap, type MeterMap } from '../../core/time/meter';
import { mergeDirty } from '../../core/ops/dirty';
import type { ChangeSet, DirtyScopes, Op } from '../../core/ops/types';
import type { KeyId, NoteId } from '../../core/model/types';
import type { MessageRef, Unsubscribe } from '../../core/util/types';
import type {
  FrameScheduler,
  TimeAxis,
  TimelineColumnDef,
  TimelineColumnInstance,
  TimelineColumnView,
  TimelineDecoration,
  TimelineDecorationProvider,
  TimelineHit,
  TimelineHost,
  TimelineHostApi,
  TimelineLayer,
  TimelinePointerEvent,
  TimelineViewConfig,
  CreateTimelineHost,
} from '../types';
import {
  bottomForAnchor,
  bottomForPlayhead,
  clampBottomBeat,
  clampDiv,
  clampPxPerBeat,
  createStripAxis,
  createTimeAxis,
  currentDpr,
  cycleSnapDiv,
  normalizeWheel,
  scrollBeatsForDelta,
  snapBeat,
  wheelZoomFactor,
  WHEEL_NOTCH_PX,
  type AxisContext,
  type MutableTimeAxis,
} from './axis';
import {
  createGutterView,
  GUTTER_DEF_ID,
  GUTTER_INSTANCE_ID,
  GUTTER_WIDTH,
  timeRangeF64,
  type GutterView,
  type RangeModifiers,
  type TimeRange,
} from './gutterColumn';
import { isGlobalDirty, type DocScopeView } from './docChange';
import { createOverviewStrip, OVERVIEW_WIDTH, type OverviewData, type OverviewStrip } from './overviewStrip';
import { FALLBACK_THEME, readTimelineTheme, type TimelineTheme } from './theme';
import catalogue, { m } from './timeline.messages';
import {
  createWaveformView,
  WAVEFORM_DEF_ID,
  WAVEFORM_INSTANCE_ID,
  WAVEFORM_ORDER,
  WAVEFORM_WIDTH,
  type WaveformSource,
} from './waveformColumn';

export type { TimeRange } from './gutterColumn';
export type { OverviewData } from './overviewStrip';
export type { WaveformSource } from './waveformColumn';

// ---------------------------------------------------------------------------------------------------------------
// Additive public types
// ---------------------------------------------------------------------------------------------------------------

/**
 * Optional extras a column view may implement (the host feature-tests for them).
 *
 * Scroll cache (UX-027, W3-03): a view with `scrollCache: true` promises that its static and content layers are pure
 * functions of beats (nothing pinned to the visible edges, the axis it is handed for them only maps beats to y): the
 * host then draws them for a range taller than the visible one (a strip, with a TimeAxis of that range) and scrolls
 * the cached pixels, so following the playhead or wheel scrolling redraws nothing until the view leaves the strip.
 * Anything pinned to the visible area (rulers, value-axis labels, empty-state messages) goes in `drawFixed`, which the
 * host calls with its live axis after the cached layers and before the overlay. The overlay layer, hit-tests and
 * pointer events always get the live axis. Views without the flag get the live axis for every layer and a strip of
 * exactly the visible range, redrawn on every axis change (the P0 behaviour).
 */
export interface ScrollCacheView {
  readonly scrollCache?: boolean;
  drawFixed?(ctx: CanvasRenderingContext2D, axis: TimeAxis, widthPx: number): void;
}

/**
 * Content scoping (UX-029, W3-03): a view that can tell whether a document change can reach its content layer,
 * so a commit redraws only the columns whose pixels can change. The host asks only about line-scoped changes
 * (tempo, timing, the line set, effects and the other global scopes redraw every content layer without asking);
 * `range` is the padded beat range the content layer is drawn for (the strip's range) and `ops` the ChangeSet's
 * ops when the caller passed them. Absent = always redrawn. See `docChange.ts` (note beat windows) — when
 * unsure, answer true.
 */
export type { DocScopeView } from './docChange';

/** A document change the host asks the scoped views about (UX-029). */
interface DocChangeRequest {
  readonly dirty: DirtyScopes;
  /** The ChangeSet's ops when the caller passed them (absent = the views' beat windows stay unknown). */
  readonly ops: readonly Op[] | undefined;
}

export type MarqueeMode = 'replace' | 'add' | 'toggle' | 'subtract';

/** Merged answer of every column's `itemsInRect`. */
export interface MarqueeItems {
  readonly notes: readonly NoteId[];
  readonly keys: readonly KeyId[];
  readonly segments: readonly KeyId[];
}

export interface MarqueeEvent {
  readonly phase: 'update' | 'end' | 'cancel';
  /** Instance id that started the marquee. */
  readonly owner: string;
  readonly mode: MarqueeMode;
  /** Beat range (f64, beat0 <= beat1). */
  readonly beat0: number;
  readonly beat1: number;
  readonly items: MarqueeItems;
  /** Instance ids whose `itemsInRect` was asked. */
  readonly columns: readonly string[];
}

export interface TimelineSeekEvent {
  readonly beat: number;
  readonly source: 'gutter' | 'overview';
  readonly phase: 'start' | 'move' | 'end';
}

export interface TimeRangeEvent {
  readonly range: TimeRange | null;
  readonly phase: 'live' | 'commit' | 'cancel';
}

export interface GutterContextMenuEvent {
  readonly beat: number;
  /** The beat snapped to the current grid. */
  readonly snapped: TimeRange['start'];
  readonly clientX: number;
  readonly clientY: number;
  readonly timeRange: TimeRange | null;
  readonly playhead: number;
}

export interface ColumnLayout {
  readonly id: string;
  readonly defId: string;
  /** Left edge: root px for pinned columns, scrolling-area content px otherwise. */
  readonly x: number;
  readonly width: number;
  readonly pinned: boolean;
  readonly title: string;
  readonly header: ComponentType<{ readonly instanceId: string }> | null;
  /** Element to portal the column's header into (null before the host is mounted). */
  readonly headerEl: HTMLElement | null;
}

export interface TimelineHostEvents {
  seek: TimelineSeekEvent;
  marquee: MarqueeEvent;
  timeRange: TimeRangeEvent;
  loop: TimeRangeEvent;
  gutterContextMenu: GutterContextMenuEvent;
  decorationHover: {
    readonly decoration: TimelineDecoration | null;
    readonly clientX: number;
    readonly clientY: number;
  };
  columnResize: { readonly instanceId: string; readonly widthPx: number | null };
  layout: readonly ColumnLayout[];
  follow: { readonly following: boolean };
  /** RPE preset Ctrl+wheel not consumed by a column (line switching, W2-07). */
  wheel: { readonly kind: 'ctrl'; readonly steps: number; readonly instanceId: string | null; readonly beat: number };
  /**
   * Horizontal zoom/pan over a column that does not implement zoomX/panX. `dxPx` uses the panX convention (positive
   * moves the view right, like scrollLeft += dxPx). A listener that handles the gesture calls `consume()`; an
   * unconsumed 'panX' scrolls the columns area sideways instead.
   */
  horizontal: {
    readonly kind: 'zoomX' | 'panX';
    readonly instanceId: string;
    readonly factor: number;
    readonly dxPx: number;
    readonly x: number;
    consume(): void;
  };
}

export interface TimelineHostOptions {
  /** Translates host strings and column titles (`services.i18n.tr`); null = English catalogue / raw keys. */
  readonly translate: ((ref: MessageRef) => string) | null;
  /** settings.editing.scrollSensitivity: beats per 100 px of normalised wheel deltaY. */
  readonly scrollSensitivity: number;
  readonly wheelPreset: 'default' | 'rpe';
  /** Alt-free variant (Settings > Keyboard): Alt+wheel does not cycle the snap. */
  readonly altFree: boolean;
  /** macOS conventions (Cmd = Ctrl, Shift+wheel is horizontal). Default: detected. */
  readonly mac: boolean;
  /** Reduced motion: 'system' follows prefers-reduced-motion. */
  readonly reducedMotion: boolean | 'system';
  /** Height of the header row above the canvases; 'auto' = 52 px when any column has a header, else 0. */
  readonly headerHeight: number | 'auto';
  /** Show the built-in waveform column while a waveform source is set. */
  readonly waveformColumn: boolean;
  readonly overview: boolean;
}

export interface TimelineHostDeps {
  readonly scheduler: FrameScheduler;
  readonly options?: Partial<TimelineHostOptions>;
}

/** The host with its additive API (what `createTimelineHost` returns). */
export interface TimelineHostController extends TimelineHost {
  readonly axis: MutableTimeAxis;
  setOptions(p: Partial<TimelineHostOptions>): void;
  readonly options: TimelineHostOptions;
  on<K extends keyof TimelineHostEvents>(type: K, cb: (e: TimelineHostEvents[K]) => void): Unsubscribe;
  /** Current view (the last setView merged with the host's own scroll/zoom/snap changes). */
  view(): TimelineViewConfig;
  readonly playhead: number;
  setPlaying(playing: boolean): void;
  readonly playing: boolean;
  /** True while the view follows the playhead (followPlayhead on and not paused by a manual scroll). */
  readonly following: boolean;
  resumeFollow(): void;
  /**
   * Redraw after a document change: tempo/timing also redraw the static layer, and the content of the columns
   * the change can reach (every column when the change is not line-scoped; see `DocScopeView`). The panel passes
   * the full ChangeSet so the views can narrow their answer with its ops.
   */
  applyChangeSet(cs: Pick<ChangeSet, 'dirty'> & Partial<Pick<ChangeSet, 'ops'>>): void;
  /** Engine revision getter, checked every frame (content redraw when it changes). */
  setRevisionSource(fn: (() => number) | null): void;
  setWaveform(src: WaveformSource | null): void;
  readonly waveform: WaveformSource | null;
  setOverview(data: OverviewData | null): void;
  getTimeRange(): TimeRange | null;
  setTimeRange(range: TimeRange | null): void;
  /** Restore persisted user widths (instance id -> px); missing ids use their default. */
  setColumnWidths(widths: Readonly<Record<string, number>>): void;
  columnWidths(): Readonly<Record<string, number>>;
  getLayout(): readonly ColumnLayout[];
  headerSlot(instanceId: string): HTMLElement | null;
  /** Scroll the column area so a column is visible. */
  revealColumn(instanceId: string): void;
  /** Zoom and scroll so [beat0, beat1] fills most of the height (Frame selection). */
  frameRange(beat0: number, beat1: number): void;
  /**
   * Merged `itemsInRect` of the scrolling columns for a rectangle (content px), e.g. the RPE Shift-click marquee.
   * x0 = -Infinity / x1 = +Infinity leave that side open: the columns get the infinity unchanged and take every item
   * on that side, also outside their visible horizontal range (`queryRect(b0, b1, -Infinity, Infinity)` is a
   * time-range query, like the gutter's range selection).
   */
  queryRect(
    beat0: number,
    beat1: number,
    x0: number,
    x1: number,
  ): MarqueeItems & { readonly columns: readonly string[] };
  /** Last hover position over a column, or null. */
  hoverInfo(): { readonly instanceId: string; readonly x: number; readonly y: number; readonly beat: number } | null;
  /** Cancel the active pointer gesture (as Esc does). */
  cancelGesture(): void;
  /** Re-read the design tokens (theme switch). */
  refreshTheme(): void;
  readonly theme: TimelineTheme;
  readonly meter: MeterMap;
  readonly root: HTMLElement | null;
}

// ---------------------------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------------------------

export const DEFAULT_VIEW: TimelineViewConfig = Object.freeze({
  pxPerBeat: 120,
  bottomBeat: 0,
  snapDiv: 4,
  beatSnap: true,
  playheadRatio: 0.25,
  followPlayhead: true,
  meter: Object.freeze([Object.freeze({ beat: 0, beatsPerBar: 4 })]),
  waveformOpacity: 0.5,
});

export const DEFAULT_OPTIONS: TimelineHostOptions = Object.freeze({
  translate: null,
  scrollSensitivity: 1,
  wheelPreset: 'default',
  altFree: false,
  mac: detectMac(),
  reducedMotion: 'system',
  headerHeight: 'auto',
  waveformColumn: true,
  overview: true,
});

/** Header row height used by headerHeight 'auto' when a column has a header. */
export const AUTO_HEADER_PX = 52;
const LAYERS: readonly TimelineLayer[] = ['static', 'content', 'overlay'];
/** Pointer travel before a press on empty space becomes a marquee / drag (auto-scroll starts too). */
const DRAG_THRESHOLD_PX = 4;
/**
 * Distance from the top/bottom edge where dragging auto-scrolls time, and from the left/right edge of the columns
 * area where a marquee scrolls the columns sideways.
 */
const AUTOSCROLL_EDGE_PX = 28;
/** Auto-scroll speed at the very edge, CSS px per ms (doubles when the pointer is outside). */
const AUTOSCROLL_PX_PER_MS = 0.9;
/** Minimum time between onViewChange reports caused by follow mode. */
const FOLLOW_EMIT_MS = 250;
/**
 * Minimum time between onViewChange reports during a live scroll / zoom gesture (UX-030 gesture coalescing).
 * The host's own draw follows the axis directly; the report is the panel's store mirror (`setTimeline`), whose
 * subscribers (problem badges, inspector field problems, checker decorations) re-render on every write. One
 * report per gesture step therefore costs a full app-wide React pass per wheel event — the measured scroll cost
 * on the 30k chart. While a gesture is live the reports coalesce to this rate (and flush as soon as it settles);
 * a discrete step outside a gesture still reports on its own frame.
 */
const USER_EMIT_MS = 400;
/** How long a reported view config is remembered to recognise late echoes in `setView`. */
const ECHO_WINDOW_MS = 1000;
const MAX_VIEW_REPORTS = 16;
/** Smallest grid step, in overview strip px, a loop drag there snaps to (coarser: whole beats). */
const OVERVIEW_MIN_SNAP_PX = 2;
const REVEAL_MS = 180;
const RESIZE_STEP_PX = 8;
/** A gutter time range [start, end) selects items before its end: the end beat belongs to the next range. */
const RANGE_END_EPS = 1e-6;
/** Strip margin in the direction the view moves: this share of the height (at least STRIP_MIN_MARGIN_PX). */
const STRIP_MARGIN_RATIO = 1;
const STRIP_MIN_MARGIN_PX = 256;
/** Strip margin on the side the view moves away from. */
const STRIP_NEAR_PX = 32;
/** Tallest strip, device px (keeps the hidden canvases well inside every browser's limits). */
const MAX_STRIP_DEVICE_PX = 8192;
/** A scroll this recent gives new strips margins in its direction. */
const SCROLL_RECENT_MS = 400;
/** After this many frames in a row with content changes, strips get no margins. */
const CONTENT_STREAK_NO_MARGIN = 3;
/** Wheel / pinch zoom: the strips are shown scaled until no zoom step came for this long, then redrawn. */
const ZOOM_SETTLE_MS = 140;
/** Content scoping: glyph half-extents are padded into the beat range a change is tested against (UX-029). */
const GLYPH_PAD_PX = 48;
/** While playing, the overview strip redraws for the moving playhead / view box at most this often. */
const OVERVIEW_PLAYING_MS = 100;

function detectMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { readonly userAgentData?: { readonly platform?: string } };
  const platform = nav.userAgentData?.platform ?? nav.platform;
  return /mac|iphone|ipad|ipod/i.test(platform);
}

function clock(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

function prefersReducedMotion(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** The view fields the host changes by itself (scroll, zoom, Alt+wheel snap). */
interface AxisFields {
  readonly pxPerBeat: number;
  readonly bottomBeat: number;
  readonly snapDiv: number;
}

function sameAxisFields(a: AxisFields, b: AxisFields): boolean {
  return a.pxPerBeat === b.pxPerBeat && a.bottomBeat === b.bottomBeat && a.snapDiv === b.snapDiv;
}

function sameMeter(a: TimelineViewConfig['meter'], b: TimelineViewConfig['meter']): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x === undefined || y === undefined || x.beat !== y.beat || x.beatsPerBar !== y.beatsPerBar) return false;
  }
  return true;
}

function compileMeter(meter: TimelineViewConfig['meter']): MeterMap {
  const marks = meter
    .filter((mk) => Number.isFinite(mk.beat))
    .map((mk) => ({ t: snapBeat(mk.beat, 1920), beatsPerBar: mk.beatsPerBar }));
  return createMeterMap(marks);
}

function interpolate(text: string, params: Readonly<Record<string, string | number>> | undefined): string {
  if (params === undefined) return text;
  return text.replace(/\{(\w+)\}/g, (all, name: string) => {
    const v = params[name];
    return v === undefined ? all : String(v);
  });
}

/** An opaque 2D context (no alpha channel: the compositor treats the canvas as opaque). */
function context2d(c: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return c.getContext('2d', { alpha: false });
  } catch {
    return null;
  }
}

function isEditableTarget(t: EventTarget | null): boolean {
  if (typeof Element === 'undefined' || !(t instanceof Element)) return false;
  return t.closest('input, textarea, select, [contenteditable="true"], [data-tl-header]') !== null;
}

// ---------------------------------------------------------------------------------------------------------------
// Internal structures
// ---------------------------------------------------------------------------------------------------------------

/** An offscreen strip (display: none, so the views' token reads through `ctx.canvas` still resolve). */
interface StripCanvas {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D | null;
}

interface SlotDom {
  readonly el: HTMLDivElement;
  readonly headerEl: HTMLDivElement;
  readonly bodyEl: HTMLDivElement;
  readonly handleEl: HTMLDivElement;
  /** The one visible, opaque canvas of the column (data-layer="surface"). */
  readonly surface: HTMLCanvasElement;
  readonly surfaceCtx: CanvasRenderingContext2D | null;
  /** Static layer of the strip range. */
  readonly staticStrip: StripCanvas;
  /** Static + content of the strip range: what a compose copies onto the surface. */
  readonly baseStrip: StripCanvas;
  backingW: number;
  backingH: number;
  readonly cleanup: () => void;
}

/** Beat range and pixel geometry the strips were drawn for. */
interface StripGeom {
  readonly bottomBeat: number;
  /** CSS px (heightD / dpr exactly). */
  readonly heightCss: number;
  /** Device px. */
  readonly heightD: number;
  readonly widthD: number;
  readonly pxPerBeat: number;
  readonly dpr: number;
  readonly width: number;
  /** The host's live axis was used (views that do not cache scroll): valid only for that exact view. */
  readonly live: boolean;
  readonly axisVersion: number;
}

interface Slot {
  inst: TimelineColumnInstance;
  def: TimelineColumnDef;
  view: TimelineColumnView;
  readonly builtin: boolean;
  readonly pinned: boolean;
  x: number;
  width: number;
  title: string;
  dom: SlotDom | null;
  readonly dirty: Set<TimelineLayer>;
  laidOutWidth: number;
  laidOutAxis: number;
  failed: boolean;
  /** Geometry of the drawn strips (null: nothing valid drawn). */
  strip: StripGeom | null;
  /** The axis handed to the view for strip draws (scroll-caching views only). */
  stripAxis: MutableTimeAxis | null;
  /** Consecutive frames whose content was invalidated (no strip margins while it keeps changing). */
  contentStreak: number;
  /** Axis version of the last compose (-1: never). */
  composedAxis: number;
}

interface PointerSnapshot {
  readonly clientX: number;
  readonly clientY: number;
  readonly buttons: number;
  readonly shift: boolean;
  readonly ctrl: boolean;
  readonly alt: boolean;
  readonly detail: number;
}

type Press =
  | {
      readonly kind: 'view' | 'armed';
      readonly pointerId: number;
      readonly slot: Slot;
      readonly start: PointerSnapshot;
      readonly startBeat: number;
      /** Content x (scrolling area) at the press, for marquees. */
      readonly startContentX: number;
      moved: boolean;
      last: PointerSnapshot;
    }
  | {
      readonly kind: 'marquee';
      readonly pointerId: number;
      readonly slot: Slot;
      readonly owner: string;
      readonly ownerNotified: boolean;
      readonly mode: MarqueeMode;
      readonly start: PointerSnapshot;
      readonly anchorBeat: number;
      readonly anchorX: number;
      moved: boolean;
      last: PointerSnapshot;
      curX: number;
      curY: number;
    }
  | {
      readonly kind: 'pan';
      readonly pointerId: number;
      readonly slot: Slot | null;
      readonly start: PointerSnapshot;
      readonly startBottom: number;
      moved: boolean;
      last: PointerSnapshot;
      lastClientX: number;
    }
  | {
      readonly kind: 'overview';
      readonly pointerId: number;
      readonly start: PointerSnapshot;
      /** What the press does: seek (click / drag) or set the loop (loop modifier). */
      readonly mode: 'seek' | 'loop';
      /** View state at the press, restored when a seek drag is cancelled (Esc). */
      readonly startBottom: number;
      readonly followSuspended: boolean;
      moved: boolean;
      last: PointerSnapshot;
    };

type Region =
  | { readonly kind: 'column'; readonly slot: Slot; readonly x: number; readonly y: number; readonly contentX: number }
  | { readonly kind: 'overview'; readonly y: number }
  /**
   * Inside the timeline but over no column: empty space right of the last column, or (`scrollbar`) the strip below
   * the canvases that holds the columns area's horizontal scrollbar. Presses there are left to the browser.
   */
  | { readonly kind: 'gap'; readonly y: number; readonly scrollbar: boolean }
  | { readonly kind: 'header' | 'none' };

function snapshot(e: MouseEvent, mac: boolean): PointerSnapshot {
  return {
    clientX: e.clientX,
    clientY: e.clientY,
    buttons: e.buttons,
    shift: e.shiftKey,
    // macOS: Cmd is the toggle modifier; Ctrl+click is a right-click there (DESIGN.md §8.1).
    ctrl: mac ? e.metaKey : e.ctrlKey,
    alt: e.altKey,
    detail: e.detail,
  };
}

function marqueeModeOf(s: Pick<PointerSnapshot, 'shift' | 'ctrl' | 'alt'>): MarqueeMode {
  if (s.alt || (s.ctrl && s.shift)) return 'subtract';
  if (s.ctrl) return 'toggle';
  if (s.shift) return 'add';
  return 'replace';
}

let hostCounter = 0;

// ---------------------------------------------------------------------------------------------------------------
// Host
// ---------------------------------------------------------------------------------------------------------------

export function createTimelineHostController(deps: TimelineHostDeps): TimelineHostController {
  const scheduler = deps.scheduler;
  const frameId = `timeline-host-${String(++hostCounter)}`;
  let options: TimelineHostOptions = { ...DEFAULT_OPTIONS, ...deps.options };

  let view: TimelineViewConfig = DEFAULT_VIEW;
  let meterMap: MeterMap = compileMeter(view.meter);
  let theme: TimelineTheme = FALLBACK_THEME;
  let playhead = 0;
  let playing = false;
  let followSuspended = false;
  /** A playhead scrub in the gutter is in progress (follow holds still). */
  let scrubbing = false;

  const context: AxisContext = {
    get meter() {
      return meterMap;
    },
    get theme() {
      return theme;
    },
    get playheadBeat() {
      return playhead;
    },
    get view() {
      return currentView();
    },
    get waveform() {
      return waveformSource;
    },
  };
  const axis = createTimeAxis(
    {
      pxPerBeat: view.pxPerBeat,
      bottomBeat: view.bottomBeat,
      snapDiv: view.snapDiv,
      beatSnap: view.beatSnap,
      dpr: currentDpr(),
    },
    context,
  );

  // DOM
  let container: HTMLElement | null = null;
  let root: HTMLDivElement | null = null;
  let gutterArea: HTMLDivElement | null = null;
  let scroller: HTMLDivElement | null = null;
  let track: HTMLDivElement | null = null;
  let overviewEl: HTMLDivElement | null = null;
  let overviewCanvas: HTMLCanvasElement | null = null;
  let overviewCtx: CanvasRenderingContext2D | null = null;
  /** Cached base of the overview strip and what it was drawn for. */
  let overviewBase: StripCanvas | null = null;
  let overviewBaseKey = '';
  let overviewBaseTheme: TimelineTheme | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let themeObserver: MutationObserver | null = null;
  const domCleanups: Array<() => void> = [];
  let unregisterFrame: Unsubscribe | null = null;
  let disposed = false;

  // Columns
  let defs: readonly TimelineColumnDef[] = [];
  let slots: Slot[] = [];
  const slotById = new Map<string, Slot>();
  const userWidths = new Map<string, number>();
  let inRelayout = false;
  let relayoutAgain = false;
  let headerPx = 0;
  let rootW = 0;
  let rootH = 0;
  let scrollbarPx = 0;
  let needsMeasure = true;

  // Decorations, data
  let providers: readonly TimelineDecorationProvider[] = [];
  let providerUnsubs: Unsubscribe[] = [];
  let revisionSource: (() => number) | null = null;
  let lastRevision: number | null = null;
  let waveformSource: WaveformSource | null = null;
  let overviewData: OverviewData | null = null;
  let timeRange: TimeRange | null = null;

  // Interaction
  let press: Press | null = null;
  let hoverSlot: Slot | null = null;
  let hover: { instanceId: string; x: number; y: number; beat: number } | null = null;
  let lastClient: { x: number; y: number } = { x: 0, y: 0 };
  let wheelAcc = 0;
  let wheelAccAt = 0;
  let marqueeDirty = false;
  /** Live time-range drag in the gutter, reported as a gutter-owned marquee (DESIGN.md §5.4). */
  let rangeSel: { readonly mode: MarqueeMode; readonly range: TimeRange; dirty: boolean } | null = null;
  let gestureKeyListener: ((e: KeyboardEvent) => void) | null = null;

  // Frame state
  let drawnAxisVersion = -1;
  let overviewDirty = true;
  /** The overview only shows a moved playhead / view box (throttled while playing). */
  let overviewSoftDirty = false;
  let overviewDrawnAt = Number.NEGATIVE_INFINITY;
  /** Scroll tracking for strip margins: last direction (1 = later beats) and when (frame clock). */
  let scrollDir = 0;
  let scrollAt = Number.NEGATIVE_INFINITY;
  let lastFrameBottom = Number.NaN;
  let lastFramePpb = Number.NaN;
  /** A wheel / pinch zoom step arrived since the last frame; strips show scaled until `zoomUntil` (frame clock). */
  let zoomPending = false;
  let zoomUntil = Number.NEGATIVE_INFINITY;
  /**
   * Content scope of the document changes since the last engine revision was seen (UX-029): the revision bumps
   * with every ChangeSet, so the engine-driven redraw can use their scopes instead of repainting everything.
   * 'all' = a global change arrived (or a tempo/timing one), null = nothing yet (an unexplained revision
   * redraws every content layer).
   */
  let pendingRevision: DocChangeRequest | 'all' | null = null;
  let viewEmit: { follow: boolean } | null = null;
  /** Recently reported axis fields (oldest first), to recognise late echoes in setView. */
  const viewReports: Array<AxisFields & { readonly at: number }> = [];
  /**
   * Axis fields the panel last saw: those of the previous setView or of the host's latest report, whichever came
   * later (null before either). A setView field equal to it carries no new intent.
   */
  let lastSynced: AxisFields | null = null;
  let lastFollowEmitAt = Number.NEGATIVE_INFINITY;
  /** Last onViewChange report at any time (UX-030: the user-gesture coalesce window). */
  let lastViewEmitAt = Number.NEGATIVE_INFINITY;
  let lastNow = 0;
  let anim: { from: number; to: number; t0: number | null } | null = null;
  let autoscrollAt: number | null = null;
  /** Sub-pixel remainder of the sideways auto-scroll (scrollLeft may round to whole pixels). */
  let autoscrollXAcc = 0;
  /** A reveal / frame request made before the first layout (no height yet). */
  let pendingView:
    | { readonly kind: 'reveal'; readonly beat: number; readonly where: 'center' | 'playhead' }
    | { readonly kind: 'frame'; readonly beat0: number; readonly beat1: number }
    | null = null;

  const listeners = new Map<keyof TimelineHostEvents, Set<(e: never) => void>>();
  const viewListeners = new Set<(cfg: TimelineViewConfig) => void>();

  // -------------------------------------------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------------------------------------------

  function emit<K extends keyof TimelineHostEvents>(type: K, e: TimelineHostEvents[K]): void {
    const set = listeners.get(type);
    if (set === undefined) return;
    for (const cb of [...set]) {
      try {
        (cb as (ev: TimelineHostEvents[K]) => void)(e);
      } catch (err) {
        console.error(`timeline host: '${type}' listener failed`, err);
      }
    }
  }

  function tr(ref: MessageRef): string {
    if (options.translate !== null) return options.translate(ref);
    const prefix = `${catalogue.ns}.`;
    if (ref.key.startsWith(prefix)) {
      const text = catalogue.messages[ref.key.slice(prefix.length)];
      if (text !== undefined) return interpolate(text, ref.params);
    }
    return ref.key;
  }

  /** A hint naming the loop modifier ({loop}): Alt / Option, or Ctrl+Shift / Cmd+Shift in the Alt-free variant. */
  function hint(key: MessageRef['key']): string {
    const loopKey = options.altFree
      ? options.mac
        ? m.loopKeyCmdShift
        : m.loopKeyCtrlShift
      : options.mac
        ? m.loopKeyOption
        : m.loopKeyAlt;
    return tr({ key, params: { loop: tr({ key: loopKey }) } });
  }

  function reducedMotion(): boolean {
    return options.reducedMotion === 'system' ? prefersReducedMotion() : options.reducedMotion;
  }

  function currentView(): TimelineViewConfig {
    if (
      view.pxPerBeat === axis.pxPerBeat &&
      view.bottomBeat === axis.bottomBeat &&
      view.snapDiv === axis.snapDiv &&
      view.beatSnap === axis.beatSnap
    ) {
      return view;
    }
    view = {
      ...view,
      pxPerBeat: axis.pxPerBeat,
      bottomBeat: axis.bottomBeat,
      snapDiv: axis.snapDiv,
      beatSnap: axis.beatSnap,
    };
    return view;
  }

  function isFollowing(): boolean {
    return view.followPlayhead && !followSuspended;
  }

  function requestFrame(): void {
    if (root !== null && !disposed) scheduler.request(frameId);
  }

  function markSlot(slot: Slot, layers: readonly TimelineLayer[] = LAYERS): void {
    for (const l of layers) slot.dirty.add(l);
  }

  function markAll(layers: readonly TimelineLayer[] = LAYERS): void {
    for (const s of slots) markSlot(s, layers);
    requestFrame();
  }

  /**
   * Content of every column that draws document data, after a ChangeSet or an engine revision. The built-in
   * gutter (its content is the decorations, whose providers notify the host themselves) and the waveform column (no
   * content layer) are left alone (UX-029: on a large chart the problem decorations are costly to collect).
   *
   * Incremental (UX-029): a line-scoped change redraws only the columns that answer `DocScopeView.contentAffected`
   * with true (absent = always); `change` is null for an unexplained engine revision, which redraws everything.
   */
  function markDocContent(change: DocChangeRequest | null): void {
    const all = change === null || isGlobalDirty(change.dirty);
    for (const s of slots) {
      if (s.view === gutterView || s.view === waveformView) continue;
      if (!all && !contentAffected(s, change)) continue;
      markSlot(s, ['content']);
    }
    requestFrame();
  }

  /** The view's `contentAffected` answer, defaulting to "yes" and treating a throwing view as "yes". */
  function contentAffected(slot: Slot, change: DocChangeRequest): boolean {
    const v = slot.view as TimelineColumnView & Partial<DocScopeView>;
    const fn = v.contentAffected;
    if (fn === undefined) return true;
    try {
      return fn.call(v, change.dirty, change.ops, paddedStripRange(slot));
    } catch (err) {
      console.error('timeline host: contentAffected failed', err);
      return true;
    }
  }

  /**
   * The beat range the slot's content layer is drawn for: the strip's range (exactly the visible one for views
   * without a strip yet), padded so a glyph just outside cannot paint a row inside.
   */
  function paddedStripRange(slot: Slot): readonly [number, number] {
    const g = slot.strip;
    const ppb = g === null ? axis.pxPerBeat : g.pxPerBeat;
    const pad = GLYPH_PAD_PX / (ppb > 0 && Number.isFinite(ppb) ? ppb : 1);
    const lo = g === null ? axis.range[0] : g.bottomBeat;
    const hi = g === null ? axis.range[1] : g.bottomBeat + g.heightCss / g.pxPerBeat;
    return [lo - pad, hi + pad];
  }

  /** Two line-scoped changes between frames become one (UX-029): union of the scopes, concatenation of the ops. */
  function mergeChange(a: DocChangeRequest, b: DocChangeRequest): DocChangeRequest {
    return {
      dirty: mergeDirty(a.dirty, b.dirty),
      ops: a.ops === undefined || b.ops === undefined ? undefined : [...a.ops, ...b.ops],
    };
  }

  function queueViewEmit(follow: boolean): void {
    if (viewEmit === null) viewEmit = { follow };
    else if (!follow) viewEmit.follow = false;
    requestFrame();
  }

  function flushViewEmit(now: number, force: boolean): void {
    if (viewEmit === null) return;
    if (!force) {
      if (viewEmit.follow) {
        if (now - lastFollowEmitAt < FOLLOW_EMIT_MS) return;
        lastFollowEmitAt = now;
      } else if (now - lastViewEmitAt < USER_EMIT_MS && gestureActive(now)) {
        // UX-030 gesture coalescing: one report per USER_EMIT_MS while the view is being dragged / wheeled.
        // Outside a gesture (a discrete step, a settled axis) the report goes out on this frame.
        return;
      }
    }
    lastViewEmitAt = now;
    viewEmit = null;
    const cfg = currentView();
    rememberReport(cfg);
    for (const cb of [...viewListeners]) {
      try {
        cb(cfg);
      } catch (err) {
        console.error('timeline host: onViewChange listener failed', err);
      }
    }
  }

  /** A scroll / zoom gesture is still in progress (the same window the strip margins and the overview use). */
  function gestureActive(now: number): boolean {
    return now - scrollAt <= SCROLL_RECENT_MS || now < zoomUntil;
  }

  function pruneReports(t: number): void {
    while (viewReports.length > MAX_VIEW_REPORTS) viewReports.shift();
    while (viewReports.length > 0 && t - (viewReports[0]?.at ?? t) > ECHO_WINDOW_MS) viewReports.shift();
  }

  function rememberReport(cfg: TimelineViewConfig): void {
    const t = clock();
    const fields: AxisFields = { pxPerBeat: cfg.pxPerBeat, bottomBeat: cfg.bottomBeat, snapDiv: clampDiv(cfg.snapDiv) };
    viewReports.push({ ...fields, at: t });
    pruneReports(t);
    // The panel has been told: a later setView that differs from this report is a new intent, also when it repeats
    // what an earlier setView asked for (e.g. "bottom 0" again after the user scrolled away from it).
    lastSynced = fields;
  }

  /**
   * Which axis fields (zoom, bottom, snap) of an incoming setView config to apply. The view config is a two-way
   * binding, so a field is applied only when it carries a new intent:
   *   - the config equals the host's axis: an up-to-date echo, nothing to apply (the panel has caught up);
   *   - it equals a config the host reported within ECHO_WINDOW_MS and has superseded since: a late echo of the
   *     panel's store mirror; applying it would pull the view back (jitter while scrolling), so none is applied;
   *   - otherwise each field is applied when it differs from what the panel last saw (`lastSynced`: the previous
   *     setView or the host's latest report, whichever came later). A field equal to it is stale: the panel re-sends
   *     a value it already had while the host moved on (e.g. the old bottom sent along with a new zoom before the
   *     host's scroll was reported), and applying it would undo the host's own change. Because a report updates
   *     `lastSynced`, a panel that does not mirror the reports can still ask again for a value it asked for earlier
   *     (e.g. bottom 0) once the host has reported moving away from it.
   * The first config (nothing seen yet) applies whole unless it equals the axis.
   */
  function axisFieldsToApply(asked: AxisFields): { pxPerBeat: boolean; bottomBeat: boolean; snapDiv: boolean } {
    const none = { pxPerBeat: false, bottomBeat: false, snapDiv: false };
    const cur: AxisFields = { pxPerBeat: axis.pxPerBeat, bottomBeat: axis.bottomBeat, snapDiv: axis.snapDiv };
    if (sameAxisFields(asked, cur)) {
      viewReports.length = 0;
      return none;
    }
    pruneReports(clock());
    if (viewReports.some((r) => sameAxisFields(r, asked))) return none;
    viewReports.length = 0;
    const seen = lastSynced;
    if (seen === null) return { pxPerBeat: true, bottomBeat: true, snapDiv: true };
    return {
      pxPerBeat: asked.pxPerBeat !== seen.pxPerBeat,
      bottomBeat: asked.bottomBeat !== seen.bottomBeat,
      snapDiv: asked.snapDiv !== seen.snapDiv,
    };
  }

  /** Sets the bottom beat (clamped); `source` decides follow pausing and reporting. */
  function setBottom(bottom: number, source: 'user' | 'follow' | 'api'): boolean {
    const b = axis.heightPx > 0 ? clampBottomBeat(bottom, axis.heightPx, axis.pxPerBeat) : bottom;
    if (b === axis.bottomBeat) return false;
    axis.update({ bottomBeat: b });
    if (source !== 'api') queueViewEmit(source === 'follow');
    requestFrame();
    return true;
  }

  function suspendFollow(): void {
    if (playing && isFollowing()) {
      followSuspended = true;
      emit('follow', { following: false });
    }
  }

  function applyFollow(): void {
    if (!playing || !isFollowing() || scrubbing || axis.heightPx <= 0) return;
    setBottom(bottomForPlayhead(playhead, view.playheadRatio, axis.heightPx, axis.pxPerBeat), 'follow');
  }

  function scrollBy(dBeats: number): void {
    if (dBeats === 0) return;
    anim = null;
    suspendFollow();
    setBottom(axis.bottomBeat + dBeats, 'user');
  }

  function zoomAtY(factor: number, anchorY: number, gesture = false): void {
    if (!(factor > 0) || factor === 1) return;
    anim = null;
    if (gesture) zoomPending = true;
    const anchorBeat = axis.beatAtY(anchorY);
    const ppb = clampPxPerBeat(axis.pxPerBeat * factor);
    if (ppb === axis.pxPerBeat) return;
    axis.update({ pxPerBeat: ppb });
    setBottom(bottomForAnchor(anchorBeat, anchorY, axis.heightPx, ppb), 'api');
    applyFollow();
    queueViewEmit(false);
  }

  /**
   * Scrolls so `beat` is visible: 'center' scrolls only when it is off screen or within 10 % of an edge (and pauses
   * a running follow), 'playhead' puts it at the follow position. Animated unless reduced motion asks otherwise.
   */
  function revealBeat(beat: number, where: 'center' | 'playhead', animate: boolean): void {
    const h = axis.heightPx;
    let target: number;
    if (where === 'playhead') target = bottomForPlayhead(beat, view.playheadRatio, h, axis.pxPerBeat);
    else {
      const span = axis.span;
      const margin = span * 0.1;
      if (beat >= axis.bottomBeat + margin && beat <= axis.topBeat - margin) return;
      target = beat - span / 2;
    }
    target = clampBottomBeat(target, h, axis.pxPerBeat);
    if (playing && isFollowing() && where === 'center') suspendFollow();
    if (target === axis.bottomBeat) return;
    if (!animate || reducedMotion() || root === null) {
      anim = null;
      setBottom(target, 'user');
    } else {
      anim = { from: axis.bottomBeat, to: target, t0: null };
      requestFrame();
    }
  }

  /** Zooms and scrolls so [beat0, beat1] fills 80 % of the height (at least a quarter beat). */
  function frameBeats(beat0: number, beat1: number): void {
    const lo = Math.min(beat0, beat1);
    const hi = Math.max(beat0, beat1);
    const h = axis.heightPx;
    const span = Math.max(hi - lo, 1 / 4);
    const ppb = clampPxPerBeat((h * 0.8) / span);
    anim = null;
    suspendFollow();
    axis.update({ pxPerBeat: ppb });
    setBottom((lo + hi) / 2 - h / ppb / 2, 'user');
    queueViewEmit(false);
  }

  /** Applies a reveal / frame request that arrived before the timeline had a height. */
  function applyPendingView(): void {
    const p = pendingView;
    if (p === null || axis.heightPx <= 0) return;
    pendingView = null;
    if (p.kind === 'reveal') revealBeat(p.beat, p.where, false);
    else frameBeats(p.beat0, p.beat1);
  }

  function scrollingSlots(): Slot[] {
    return slots.filter((s) => !s.pinned);
  }

  function pinnedWidth(): number {
    let w = 0;
    for (const s of slots) if (s.pinned) w += s.width;
    return w;
  }

  function overviewW(): number {
    return options.overview ? OVERVIEW_WIDTH : 0;
  }

  // -------------------------------------------------------------------------------------------------------------
  // Built-in columns
  // -------------------------------------------------------------------------------------------------------------

  const gutterView: GutterView = createGutterView({
    theme: () => theme,
    meter: () => meterMap,
    snapDiv: () => axis.snapDiv,
    playhead: () => playhead,
    decorations: (range) => decorationsIn(range),
    timeRange: () => timeRange,
    snap: (beat) => axis.snap(beat),
    onRange(range, phase, mods) {
      if (phase === 'commit') timeRange = range;
      emit('timeRange', { range: phase === 'cancel' ? timeRange : range, phase });
      rangeSelection(range, phase, mods);
      markAll(['overlay']);
    },
    onLoop(range, phase) {
      emit('loop', { range, phase });
      markAll(['overlay']);
    },
    isLoopDrag: (e) => isLoopModifier(e),
    isNoSnap: (e) => isNoSnapModifier(e),
    foreignLive: () => {
      const l = overview.liveLoop;
      return l === null ? null : { range: l, loop: true };
    },
    onSeek(beat, phase) {
      seekFrom(beat, 'gutter', phase);
    },
    onContextMenu(e) {
      emit('gutterContextMenu', {
        beat: e.beat,
        snapped: axis.snap(e.beat),
        clientX: lastClient.x,
        clientY: lastClient.y,
        timeRange,
        playhead,
      });
    },
    onHover(d) {
      const g = slotById.get(GUTTER_INSTANCE_ID);
      if (g?.dom != null) g.dom.el.title = d?.label ?? hint(m.gutterHint);
      emit('decorationHover', { decoration: d, clientX: lastClient.x, clientY: lastClient.y });
    },
    invalidate(layer) {
      const g = slotById.get(GUTTER_INSTANCE_ID);
      if (g !== undefined) markSlot(g, [layer]);
      requestFrame();
    },
  });

  const gutterInstance: TimelineColumnInstance = {
    id: GUTTER_INSTANCE_ID,
    defId: GUTTER_DEF_ID,
    widthPx: GUTTER_WIDTH,
    header: null,
    view: gutterView,
  };
  const gutterDef: TimelineColumnDef = {
    id: GUTTER_DEF_ID,
    title: { key: m.gutter },
    order: 0,
    width: { min: GUTTER_WIDTH, default: GUTTER_WIDTH, max: GUTTER_WIDTH },
    instances: () => [gutterInstance],
  };

  const waveformView = createWaveformView({
    source: () => waveformSource,
    theme: () => theme,
    opacity: () => view.waveformOpacity,
  });
  const waveformInstance: TimelineColumnInstance = {
    id: WAVEFORM_INSTANCE_ID,
    defId: WAVEFORM_DEF_ID,
    widthPx: WAVEFORM_WIDTH.default,
    header: null,
    view: waveformView,
  };
  const waveformDef: TimelineColumnDef = {
    id: WAVEFORM_DEF_ID,
    title: { key: m.waveform },
    order: WAVEFORM_ORDER,
    width: WAVEFORM_WIDTH,
    instances: () => (waveformSource !== null && options.waveformColumn ? [waveformInstance] : []),
  };

  const overview: OverviewStrip = createOverviewStrip({
    theme: () => theme,
    data: () => overviewData,
    viewRange: () => axis.range,
    playhead: () => playhead,
    decorations: (range) => decorationsIn(range),
    onSeek(beat, phase) {
      seekFrom(beat, 'overview', phase);
    },
    snap(beat, beatsPerPx) {
      // Snap to the timeline grid while a grid step is visible in the strip; otherwise to whole beats.
      const div = axis.beatSnap ? axis.snapDiv : 1;
      return axis.snapTo(beat, beatsPerPx * div > 1 / OVERVIEW_MIN_SNAP_PX ? 1 : div);
    },
    onLoop(range, phase) {
      emit('loop', { range, phase });
      markAll(['overlay']);
      overviewDirty = true;
      requestFrame();
    },
  });

  /** The loop modifier of the gutter and the overview: Alt, or Ctrl+Shift (Cmd+Shift) in the Alt-free variant. */
  function isLoopModifier(s: { readonly alt: boolean; readonly ctrl: boolean; readonly shift: boolean }): boolean {
    return s.alt || (options.altFree && s.ctrl && s.shift);
  }

  /**
   * The no-snap modifier held during a drag (the gutter's playhead scrub): Alt, or Ctrl+Shift (Cmd+Shift) in the
   * Alt-free variant (DESIGN.md §8.1: "Ctrl+Shift held during a drag replaces Alt as the no snap modifier"). Alt keeps
   * working in that variant where the window manager lets it through.
   */
  function isNoSnapModifier(s: { readonly alt: boolean; readonly ctrl: boolean; readonly shift: boolean }): boolean {
    return s.alt || (options.altFree && s.ctrl && s.shift);
  }

  /**
   * A seek from the gutter or the overview. While playing it resumes a follow paused by a manual scroll (the view
   * jumps with the playhead); otherwise (paused, or playing with follow off) an overview seek brings the target to
   * the playhead position (a gutter click is on screen already). During a playhead scrub in the gutter ('start' /
   * 'move') the view holds still, also while playing (following would move the content under the pointer); its
   * 'end' is an ordinary gutter seek.
   */
  function seekFrom(beat: number, source: TimelineSeekEvent['source'], phase: TimelineSeekEvent['phase']): void {
    playhead = beat;
    scrubbing = source === 'gutter' && phase !== 'end';
    if (scrubbing) {
      anim = null;
    } else {
      if (playing && followSuspended) {
        followSuspended = false;
        emit('follow', { following: isFollowing() });
      }
      if (playing && isFollowing()) {
        anim = null;
        applyFollow();
      } else if (source === 'overview' && axis.heightPx > 0) {
        anim = null;
        setBottom(bottomForPlayhead(beat, view.playheadRatio, axis.heightPx, axis.pxPerBeat), 'user');
      }
    }
    emit('seek', { beat, source, phase });
    markAll(['overlay']);
    overviewDirty = true;
  }

  /**
   * Esc during an overview seek drag: the strip has reported 'end' at the playhead of the press; the view goes back
   * to where it was too (a follow paused by a manual scroll before the press is paused again).
   */
  function restoreAfterOverviewSeek(p: Extract<Press, { kind: 'overview' }>): void {
    if (playing && p.followSuspended && !followSuspended && view.followPlayhead) {
      followSuspended = true;
      emit('follow', { following: false });
    }
    if (playing && isFollowing()) return;
    anim = null;
    setBottom(p.startBottom, 'user');
  }

  /**
   * DESIGN.md §5.4: dragging in the time gutter selects a time range across the lane and all columns, notes and keys
   * together. The live range is reported as 'marquee' updates (once per frame) owned by the gutter, with every
   * scrolling column asked for the half-open beat range [start, end) with an unbounded x range (x0 = -Infinity,
   * x1 = +Infinity), so a lane zoomed or panned in X still gives every note in the span, also those outside its
   * visible X range; the commit is the 'end'.
   * Shift adds, Ctrl/Cmd toggles, Ctrl+Shift subtracts (default keymap; it is the loop drag in the Alt-free variant).
   * A click (range cleared, playhead moved) leaves the selection alone.
   */
  function rangeSelection(range: TimeRange | null, phase: TimeRangeEvent['phase'], mods: RangeModifiers): void {
    const mode = marqueeModeOf({ shift: mods.shift, ctrl: mods.ctrl, alt: false });
    if (phase === 'live') {
      if (range === null) return;
      rangeSel = { mode, range, dirty: true };
      requestFrame();
      return;
    }
    const live = rangeSel;
    rangeSel = null;
    if (range !== null && phase === 'commit') emitRangeMarquee(range, mode, 'end');
    // Esc, or a drag that ended back on its start: the live preview is withdrawn.
    else if (live !== null) emitRangeMarquee(live.range, live.mode, 'cancel');
  }

  function emitRangeMarquee(range: TimeRange, mode: MarqueeMode, phase: MarqueeEvent['phase']): void {
    const [b0, b1] = timeRangeF64(range);
    const q =
      phase === 'cancel'
        ? { notes: [], keys: [], segments: [], columns: [] }
        : queryRect(b0, Math.max(b0, b1 - RANGE_END_EPS), Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY);
    emit('marquee', {
      phase,
      owner: GUTTER_INSTANCE_ID,
      mode,
      beat0: b0,
      beat1: b1,
      items: { notes: q.notes, keys: q.keys, segments: q.segments },
      columns: q.columns,
    });
  }

  /** Width of the scrolling columns together (content px; the track width). */
  function contentWidth(): number {
    let w = 0;
    for (const s of slots) if (!s.pinned) w = Math.max(w, s.x + s.width);
    return w;
  }

  function decorationsIn(range: readonly [number, number]): readonly TimelineDecoration[] {
    if (providers.length === 0) return [];
    const out: TimelineDecoration[] = [];
    for (const p of providers) {
      try {
        out.push(...p.decorations(range));
      } catch (err) {
        console.error(`timeline host: decoration provider '${p.id}' failed`, err);
      }
    }
    return out;
  }

  // -------------------------------------------------------------------------------------------------------------
  // DOM
  // -------------------------------------------------------------------------------------------------------------

  function listen<K extends keyof HTMLElementEventMap>(
    el: HTMLElement,
    type: K,
    fn: (e: HTMLElementEventMap[K]) => void,
    opts?: AddEventListenerOptions,
  ): void {
    el.addEventListener(type, fn, opts);
    domCleanups.push(() => {
      el.removeEventListener(type, fn, opts);
    });
  }

  function ensureDom(): HTMLDivElement {
    if (root !== null) return root;
    const r = document.createElement('div');
    r.dataset.timelineHost = frameId;
    r.setAttribute('role', 'group');
    r.setAttribute('aria-label', tr({ key: m.region }));
    Object.assign(r.style, {
      position: 'relative',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      background: 'var(--color-bg)',
      userSelect: 'none',
      touchAction: 'none',
      // UX-027: the timeline paints its own layer (cached strips, header readouts) and must not force the
      // document root layer to repaint with it. `paint` bounds the paint invalidation; `layout` stops the
      // scroll of the columns area from relayouting the page.
      contain: 'layout paint',
    } satisfies Partial<CSSStyleDeclaration>);

    const g = document.createElement('div');
    g.dataset.tlArea = 'pinned';
    Object.assign(g.style, { position: 'absolute', left: '0', top: '0', bottom: '0', width: '0px', zIndex: '2' });

    const sc = document.createElement('div');
    sc.dataset.tlArea = 'columns';
    sc.setAttribute('role', 'group');
    sc.setAttribute('aria-label', tr({ key: m.columns }));
    Object.assign(sc.style, {
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0px',
      right: '0px',
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollbarWidth: 'thin',
    } satisfies Partial<CSSStyleDeclaration>);
    const tk = document.createElement('div');
    Object.assign(tk.style, { position: 'relative', height: '100%', width: '0px' });
    sc.appendChild(tk);

    const ov = document.createElement('div');
    ov.dataset.tlArea = 'overview';
    ov.setAttribute('aria-hidden', 'true');
    ov.title = hint(m.overviewHint);
    Object.assign(ov.style, {
      position: 'absolute',
      right: '0',
      top: '0',
      bottom: '0',
      width: `${String(OVERVIEW_WIDTH)}px`,
      cursor: 'pointer',
      zIndex: '2',
    });
    const oc = document.createElement('canvas');
    oc.setAttribute('aria-hidden', 'true');
    Object.assign(oc.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', display: 'block' });
    ov.appendChild(oc);
    const ob = document.createElement('canvas');
    ob.dataset.tlCache = 'overview';
    ob.setAttribute('aria-hidden', 'true');
    ob.style.display = 'none';
    ov.appendChild(ob);

    r.append(g, sc, ov);
    root = r;
    gutterArea = g;
    scroller = sc;
    track = tk;
    overviewEl = ov;
    overviewCanvas = oc;
    overviewCtx = context2d(oc);
    overviewBase = { canvas: ob, ctx: context2d(ob) };

    listen(r, 'pointerdown', onPointerDown);
    listen(r, 'pointermove', onPointerMove);
    listen(r, 'pointerup', onPointerUp);
    listen(r, 'pointercancel', onPointerCancelEvent);
    listen(r, 'lostpointercapture', onLostCapture);
    listen(r, 'pointerleave', onPointerLeaveRoot);
    listen(r, 'dblclick', onDoubleClick);
    listen(r, 'contextmenu', onContextMenu);
    listen(r, 'wheel', onWheel, { passive: false });
    for (const s of slots) ensureSlotDom(s);
    syncDomOrder();
    placeSlots();
    return r;
  }

  /**
   * The column element's region, context menu and accessible name from its instance (TimelineColumnInstance.region /
   * contextMenu / label, CR-W2-03-6); a column with a region can take focus (tabindex -1). Idempotent.
   */
  function applyInstanceAttrs(slot: Slot): void {
    const el = slot.dom?.el;
    if (el === undefined) return;
    const { region, contextMenu, label } = slot.inst;
    if (region !== undefined && el.dataset.region !== region) el.dataset.region = region;
    if (region !== undefined && !el.hasAttribute('tabindex')) el.tabIndex = -1;
    if (contextMenu !== undefined && el.dataset.contextMenu !== contextMenu) el.dataset.contextMenu = contextMenu;
    const name = label ?? slot.title;
    if (el.getAttribute('aria-label') !== name) el.setAttribute('aria-label', name);
  }

  function ensureSlotDom(slot: Slot): void {
    if (slot.dom !== null || root === null) return;
    const el = document.createElement('div');
    el.dataset.tlCol = slot.inst.id;
    el.setAttribute('role', 'group');
    el.setAttribute('aria-label', slot.inst.label ?? slot.title);
    Object.assign(el.style, {
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0px',
      width: '0px',
      overflow: 'hidden',
      // UX-027: each column is its own paint containment (the visible surface, its header readout).
      contain: 'layout paint',
    });
    if (slot.inst.id === GUTTER_INSTANCE_ID) el.title = hint(m.gutterHint);

    const headerEl = document.createElement('div');
    headerEl.dataset.tlHeader = slot.inst.id;
    Object.assign(headerEl.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      right: '0',
      height: `${String(headerPx)}px`,
      overflow: 'hidden',
      // Live readouts in the header (value at the playhead) repaint only the header, never the page (UX-027).
      contain: 'strict',
    } satisfies Partial<CSSStyleDeclaration>);

    const bodyEl = document.createElement('div');
    Object.assign(bodyEl.style, {
      position: 'absolute',
      left: '0',
      right: '0',
      top: `${String(headerPx)}px`,
      bottom: '0',
      contain: 'strict',
    } satisfies Partial<CSSStyleDeclaration>);
    // One visible, opaque canvas per column: the compositor blends a single layer instead of three, and the page
    // background under it is culled (UX-027). The cached layers live in hidden strips (`StripCanvas`).
    const surface = document.createElement('canvas');
    surface.dataset.layer = 'surface';
    surface.setAttribute('aria-hidden', 'true');
    Object.assign(surface.style, { position: 'absolute', left: '0', top: '0', display: 'block', pointerEvents: 'none' });
    bodyEl.appendChild(surface);
    const strip = (name: string): StripCanvas => {
      const c = document.createElement('canvas');
      c.dataset.tlCache = name;
      c.setAttribute('aria-hidden', 'true');
      c.style.display = 'none';
      bodyEl.appendChild(c);
      return { canvas: c, ctx: context2d(c) };
    };

    // Divider + resize handle at the right edge.
    const handleEl = document.createElement('div');
    handleEl.dataset.tlResize = slot.inst.id;
    Object.assign(handleEl.style, {
      position: 'absolute',
      top: '0',
      bottom: '0',
      right: '0',
      width: '6px',
      zIndex: '3',
      display: 'flex',
      justifyContent: 'flex-end',
      outline: 'none',
    } satisfies Partial<CSSStyleDeclaration>);
    const line = document.createElement('div');
    Object.assign(line.style, {
      width: '1px',
      height: '100%',
      background: 'var(--color-divider)',
      pointerEvents: 'none',
    });
    handleEl.appendChild(line);
    const cleanup = wireResizeHandle(slot, handleEl, line);

    el.append(headerEl, bodyEl, handleEl);
    slot.dom = {
      el,
      headerEl,
      bodyEl,
      handleEl,
      surface,
      surfaceCtx: context2d(surface),
      staticStrip: strip('static'),
      baseStrip: strip('base'),
      backingW: -1,
      backingH: -1,
      cleanup,
    };
    slot.strip = null;
    applyInstanceAttrs(slot);
    updateHandleA11y(slot);
    (slot.pinned ? gutterArea : track)?.appendChild(el);
  }

  /**
   * Keeps the DOM order of the column elements equal to the layout order (focus order of headers and resize
   * handles). Elements are only moved when the order differs: moving a focused header field would blur it.
   */
  function syncDomOrder(): void {
    for (const [parent, list] of [
      [gutterArea, slots.filter((s) => s.pinned)],
      [track, slots.filter((s) => !s.pinned)],
    ] as const) {
      if (parent === null) continue;
      const els = list.flatMap((s) => (s.dom === null ? [] : [s.dom.el]));
      const current = [...parent.children].filter((c) => c instanceof HTMLElement && c.dataset.tlCol !== undefined);
      if (els.length === current.length && els.every((el, i) => current[i] === el)) continue;
      for (const el of els) parent.appendChild(el);
    }
  }

  function removeSlotDom(slot: Slot): void {
    if (slot.dom === null) return;
    slot.dom.cleanup();
    slot.dom.el.remove();
    slot.dom = null;
  }

  function resizable(slot: Slot): boolean {
    return !slot.pinned && slot.def.width.max > slot.def.width.min;
  }

  function updateHandleA11y(slot: Slot): void {
    const d = slot.dom;
    if (d === null) return;
    const h = d.handleEl;
    if (!resizable(slot)) {
      h.removeAttribute('role');
      h.removeAttribute('tabindex');
      h.removeAttribute('aria-label');
      h.style.cursor = '';
      return;
    }
    h.setAttribute('role', 'separator');
    h.setAttribute('aria-orientation', 'vertical');
    h.setAttribute('tabindex', '0');
    h.setAttribute('aria-label', tr({ key: m.resizeColumn, params: { name: slot.title } }));
    h.setAttribute('aria-valuemin', String(slot.def.width.min));
    h.setAttribute('aria-valuemax', String(slot.def.width.max));
    h.setAttribute('aria-valuenow', String(Math.round(slot.width)));
    h.style.cursor = 'col-resize';
  }

  function setUserWidth(slot: Slot, width: number | null): void {
    if (width === null) userWidths.delete(slot.inst.id);
    else userWidths.set(slot.inst.id, Math.min(slot.def.width.max, Math.max(slot.def.width.min, Math.round(width))));
    layoutColumns();
    emit('columnResize', { instanceId: slot.inst.id, widthPx: userWidths.get(slot.inst.id) ?? null });
  }

  function wireResizeHandle(slot: Slot, handle: HTMLDivElement, line: HTMLDivElement): () => void {
    let drag: { pointerId: number; startX: number; startW: number } | null = null;
    const highlight = (on: boolean): void => {
      line.style.width = on ? '2px' : '1px';
      line.style.background = on ? 'var(--color-accent)' : 'var(--color-divider)';
    };
    const down = (e: PointerEvent): void => {
      if (!resizable(slot) || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      drag = { pointerId: e.pointerId, startX: e.clientX, startW: slot.width };
      try {
        handle.setPointerCapture(e.pointerId);
      } catch {
        // capture is best effort
      }
      highlight(true);
    };
    const move = (e: PointerEvent): void => {
      if (drag?.pointerId !== e.pointerId) return;
      e.stopPropagation();
      setUserWidth(slot, drag.startW + (e.clientX - drag.startX));
    };
    const up = (e: PointerEvent): void => {
      if (drag?.pointerId !== e.pointerId) return;
      e.stopPropagation();
      drag = null;
      try {
        handle.releasePointerCapture(e.pointerId);
      } catch {
        // already released
      }
      highlight(false);
    };
    const dbl = (e: MouseEvent): void => {
      e.stopPropagation();
      if (resizable(slot)) setUserWidth(slot, null);
    };
    const key = (e: KeyboardEvent): void => {
      if (!resizable(slot)) return;
      const step = e.shiftKey ? RESIZE_STEP_PX * 4 : RESIZE_STEP_PX;
      let w: number | null | undefined;
      if (e.key === 'ArrowLeft') w = slot.width - step;
      else if (e.key === 'ArrowRight') w = slot.width + step;
      else if (e.key === 'Home') w = slot.def.width.min;
      else if (e.key === 'End') w = slot.def.width.max;
      else if (e.key === 'Delete' || e.key === 'Backspace') w = null;
      if (w === undefined) return;
      e.preventDefault();
      e.stopPropagation();
      setUserWidth(slot, w);
    };
    const enter = (): void => {
      if (resizable(slot)) highlight(true);
    };
    const leave = (): void => {
      if (drag === null && document.activeElement !== handle) highlight(false);
    };
    const focus = (): void => {
      if (handle.matches(':focus-visible')) {
        handle.style.boxShadow = 'inset 0 0 0 var(--focus-ring-width) var(--color-focus-ring)';
      }
      highlight(true);
    };
    const blur = (): void => {
      handle.style.boxShadow = '';
      highlight(false);
    };
    handle.addEventListener('pointerdown', down);
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
    handle.addEventListener('dblclick', dbl);
    handle.addEventListener('keydown', key);
    handle.addEventListener('pointerenter', enter);
    handle.addEventListener('pointerleave', leave);
    handle.addEventListener('focus', focus);
    handle.addEventListener('blur', blur);
    return () => {
      handle.removeEventListener('pointerdown', down);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      handle.removeEventListener('dblclick', dbl);
      handle.removeEventListener('keydown', key);
      handle.removeEventListener('pointerenter', enter);
      handle.removeEventListener('pointerleave', leave);
      handle.removeEventListener('focus', focus);
      handle.removeEventListener('blur', blur);
    };
  }

  // -------------------------------------------------------------------------------------------------------------
  // Layout
  // -------------------------------------------------------------------------------------------------------------

  /**
   * Thickness of the columns area's horizontal scrollbar: 0 without overflow or with overlay scrollbars (macOS,
   * mobile), else the classic scrollbar's height. Reading it forces a synchronous layout.
   */
  function readScrollbar(): number {
    if (scroller === null || scroller.offsetHeight <= 0) return 0;
    return Math.max(0, scroller.offsetHeight - scroller.clientHeight);
  }

  function measure(): void {
    needsMeasure = false;
    if (root === null) return;
    const w = root.clientWidth || container?.clientWidth || 0;
    const h = root.clientHeight || container?.clientHeight || 0;
    // The scrollbar thickness also changes with the page zoom and scrollbar styles.
    if (w === rootW && h === rootH && readScrollbar() === scrollbarPx) return;
    rootW = w;
    rootH = h;
    layoutColumns();
  }

  function computeHeaderPx(): number {
    if (options.headerHeight !== 'auto') return Math.max(0, options.headerHeight);
    return slots.some((s) => s.inst.header !== null) ? AUTO_HEADER_PX : 0;
  }

  /** Widths of the scrolling columns: user widths fixed, others flex proportionally within their min/max. */
  function flexWidths(list: readonly Slot[], available: number): number[] {
    const base = list.map((s) => {
      const user = userWidths.get(s.inst.id);
      const raw =
        user ?? (Number.isFinite(s.inst.widthPx) && s.inst.widthPx > 0 ? s.inst.widthPx : s.def.width.default);
      return Math.min(s.def.width.max, Math.max(s.def.width.min, raw));
    });
    const out = [...base];
    const flexible = list.map((s) => !userWidths.has(s.inst.id));
    const total = (): number => out.reduce((a, b) => a + b, 0);
    for (let iter = 0; iter < 8; iter++) {
      const diff = available - total();
      if (Math.abs(diff) < 0.5) break;
      let weight = 0;
      for (let i = 0; i < list.length; i++) {
        const s = list[i];
        const w = out[i];
        if (s === undefined || w === undefined || flexible[i] !== true) continue;
        if (diff > 0 ? w < s.def.width.max : w > s.def.width.min) weight += base[i] ?? 0;
      }
      if (weight <= 0) break;
      for (let i = 0; i < list.length; i++) {
        const s = list[i];
        const w = out[i];
        const b = base[i];
        if (s === undefined || w === undefined || b === undefined || flexible[i] !== true) continue;
        if (diff > 0 ? w >= s.def.width.max : w <= s.def.width.min) continue;
        out[i] = Math.min(s.def.width.max, Math.max(s.def.width.min, w + (diff * b) / weight));
      }
    }
    return out.map((w) => Math.floor(w));
  }

  function layoutColumns(): void {
    headerPx = computeHeaderPx();
    // Horizontal placement first: whether the columns overflow decides the scrollbar, and so the canvas height.
    let px = 0;
    for (const s of slots) {
      if (!s.pinned) continue;
      s.x = px;
      s.width = s.def.width.default;
      px += s.width;
    }
    const scrolling = scrollingSlots();
    const available = Math.max(0, rootW - px - overviewW());
    const widths = flexWidths(scrolling, available);
    let cx = 0;
    scrolling.forEach((s, i) => {
      s.x = cx;
      s.width = widths[i] ?? s.def.width.default;
      cx += s.width;
    });
    placeAreas();
    if (track !== null) track.style.width = `${String(cx)}px`;
    // A classic scrollbar appears or disappears with the overflow (a relayout, a column resize, a resized panel):
    // re-read it now so the canvases end above it and presses on it are never routed to a column.
    scrollbarPx = readScrollbar();
    const bodyH = Math.max(0, rootH - headerPx - scrollbarPx);
    if (axis.update({ heightPx: bodyH, dpr: currentDpr() })) {
      if (axis.heightPx > 0) setBottom(axis.bottomBeat, 'api');
      applyPendingView();
      applyFollow();
    }
    placeSlots();
    emitLayout();
    requestFrame();
  }

  /** Places the pinned area, the columns area and the overview strip. */
  function placeAreas(): void {
    const pw = pinnedWidth();
    if (gutterArea !== null) {
      gutterArea.style.width = `${String(pw)}px`;
      gutterArea.style.bottom = `${String(scrollbarPx)}px`;
    }
    if (scroller !== null) {
      scroller.style.left = `${String(pw)}px`;
      scroller.style.right = `${String(overviewW())}px`;
    }
    if (overviewEl !== null) overviewEl.style.display = options.overview ? 'block' : 'none';
  }

  function placeSlots(): void {
    if (root === null) return;
    placeAreas();
    const dpr = axis.dpr;
    for (const s of slots) {
      const d = s.dom;
      if (d === null) continue;
      d.el.style.left = `${String(s.x)}px`;
      d.el.style.width = `${String(s.width)}px`;
      d.headerEl.style.height = `${String(headerPx)}px`;
      d.headerEl.style.display = headerPx > 0 ? 'block' : 'none';
      d.bodyEl.style.top = `${String(headerPx)}px`;
      d.bodyEl.style.height = `${String(axis.heightPx)}px`;
      const bw = Math.max(0, Math.round(s.width * dpr));
      const bh = Math.max(0, Math.round(axis.heightPx * dpr));
      if (bw !== d.backingW || bh !== d.backingH) {
        d.backingW = bw;
        d.backingH = bh;
        const c = d.surface;
        c.width = bw;
        c.height = bh;
        c.style.width = `${String(s.width)}px`;
        c.style.height = `${String(axis.heightPx)}px`;
        s.strip = null;
        markSlot(s);
      }
      updateHandleA11y(s);
    }
    if (overviewCanvas !== null && options.overview) {
      const bw = Math.round(OVERVIEW_WIDTH * dpr);
      const bh = Math.round(rootH * dpr);
      if (overviewCanvas.width !== bw || overviewCanvas.height !== bh) {
        overviewCanvas.width = bw;
        overviewCanvas.height = bh;
      }
      overviewDirty = true;
    }
  }

  function layoutOf(s: Slot): ColumnLayout {
    return {
      id: s.inst.id,
      defId: s.inst.defId,
      x: s.x,
      width: s.width,
      pinned: s.pinned,
      title: s.title,
      header: s.inst.header,
      headerEl: s.dom?.headerEl ?? null,
    };
  }

  function emitLayout(): void {
    if (!listeners.has('layout')) return;
    emit('layout', slots.map(layoutOf));
  }

  function disposeView(v: TimelineColumnView): void {
    if (v === gutterView || v === waveformView) return;
    try {
      v.dispose();
    } catch (err) {
      console.error('timeline host: column view dispose failed', err);
    }
  }

  function relayout(): void {
    if (disposed) return;
    if (inRelayout) {
      relayoutAgain = true;
      return;
    }
    inRelayout = true;
    try {
      const ordered = [gutterDef, waveformDef, ...defs]
        .map((def, i) => ({ def, i }))
        .sort((a, b) => a.def.order - b.def.order || a.i - b.i);
      const next: Slot[] = [];
      const seen = new Set<string>();
      for (const { def } of ordered) {
        let list: readonly TimelineColumnInstance[];
        try {
          list = def.instances(api);
        } catch (err) {
          console.error(`timeline host: column def '${def.id}' failed to list instances`, err);
          list = [];
        }
        for (const inst of list) {
          if (seen.has(inst.id)) {
            console.error(`timeline host: duplicate column instance id '${inst.id}'`);
            continue;
          }
          seen.add(inst.id);
          const builtin = def === gutterDef || def === waveformDef;
          const title = tr(def.title);
          const old = slotById.get(inst.id);
          if (old !== undefined) {
            if (old.view !== inst.view) {
              cancelPressFor(old);
              disposeView(old.view);
              old.view = inst.view;
              old.failed = false;
              old.strip = null;
              markSlot(old);
            }
            old.inst = inst;
            old.def = def;
            old.title = title;
            applyInstanceAttrs(old);
            next.push(old);
          } else {
            next.push({
              inst,
              def,
              view: inst.view,
              builtin,
              pinned: inst.id === GUTTER_INSTANCE_ID,
              x: 0,
              width: inst.widthPx,
              title,
              dom: null,
              dirty: new Set(LAYERS),
              laidOutWidth: -1,
              laidOutAxis: -1,
              failed: false,
              strip: null,
              stripAxis: null,
              contentStreak: 0,
              composedAxis: -1,
            });
          }
        }
      }
      for (const old of slots) {
        if (seen.has(old.inst.id)) continue;
        cancelPressFor(old);
        if (hoverSlot === old) {
          hoverSlot = null;
          hover = null;
        }
        removeSlotDom(old);
        slotById.delete(old.inst.id);
        if (!old.builtin) disposeView(old.view);
      }
      slots = next;
      for (const s of slots) {
        slotById.set(s.inst.id, s);
        ensureSlotDom(s);
      }
      syncDomOrder();
      layoutColumns();
      markAll();
    } finally {
      inRelayout = false;
    }
    if (relayoutAgain) {
      relayoutAgain = false;
      relayout();
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------------------------------------------

  function cachesScroll(slot: Slot): boolean {
    return (slot.view as TimelineColumnView & ScrollCacheView).scrollCache === true;
  }

  /** Runs one layer of a view (errors are logged once per failure streak; the layer stays empty meanwhile). */
  function drawLayer(slot: Slot, layer: TimelineLayer, ctx: CanvasRenderingContext2D, ax: TimeAxis): void {
    ctx.save();
    try {
      slot.view.draw(layer, ctx, ax, slot.width);
      slot.failed = false;
    } catch (err) {
      if (!slot.failed) console.error(`timeline host: column '${slot.inst.id}' failed to draw its ${layer} layer`, err);
      slot.failed = true;
    } finally {
      ctx.restore();
    }
  }

  function drawFixed(slot: Slot, ctx: CanvasRenderingContext2D): void {
    const v = slot.view as TimelineColumnView & ScrollCacheView;
    if (v.drawFixed === undefined) return;
    ctx.save();
    try {
      v.drawFixed(ctx, axis, slot.width);
    } catch (err) {
      if (!slot.failed) console.error(`timeline host: column '${slot.inst.id}' failed to draw its fixed parts`, err);
      slot.failed = true;
    } finally {
      ctx.restore();
    }
  }

  /**
   * Strip margins (CSS px below and above the visible range) for strips drawn now: room in the direction the view
   * moves (following, or a scroll in the last moments), none for a still view or a column whose content keeps
   * changing (every frame would redraw the taller strip).
   */
  function stripMargins(slot: Slot, now: number): readonly [number, number] {
    if (!cachesScroll(slot) || slot.contentStreak >= CONTENT_STREAK_NO_MARGIN) return [0, 0];
    const far = Math.max(STRIP_MIN_MARGIN_PX, Math.round(axis.heightPx * STRIP_MARGIN_RATIO));
    if (playing && isFollowing() && !scrubbing) return [STRIP_NEAR_PX, far];
    if (now - scrollAt <= SCROLL_RECENT_MS) return scrollDir >= 0 ? [STRIP_NEAR_PX, far] : [far, STRIP_NEAR_PX];
    return [0, 0];
  }

  function newStripGeom(slot: Slot, d: SlotDom, now: number): StripGeom {
    const dpr = axis.dpr;
    const h = axis.heightPx;
    if (!cachesScroll(slot)) {
      return {
        bottomBeat: axis.bottomBeat,
        heightCss: h,
        heightD: d.backingH,
        widthD: d.backingW,
        pxPerBeat: axis.pxPerBeat,
        dpr,
        width: slot.width,
        live: true,
        axisVersion: axis.version,
      };
    }
    let [below, above] = stripMargins(slot, now);
    const maxCss = MAX_STRIP_DEVICE_PX / dpr;
    if (h + below + above > maxCss) {
      const room = Math.max(0, maxCss - h);
      const k = below + above > 0 ? room / (below + above) : 0;
      below = Math.floor(below * k);
      above = Math.floor(above * k);
    }
    const heightD = Math.max(d.backingH, Math.round((h + below + above) * dpr));
    return {
      bottomBeat: axis.bottomBeat - below / axis.pxPerBeat,
      heightCss: heightD / dpr,
      heightD,
      widthD: d.backingW,
      pxPerBeat: axis.pxPerBeat,
      dpr,
      width: slot.width,
      live: false,
      axisVersion: axis.version,
    };
  }

  /** First device row of the visible range inside the strips, or null when they do not cover it 1:1. */
  function stripOffset(slot: Slot, d: SlotDom, g: StripGeom): number | null {
    if (g.pxPerBeat !== axis.pxPerBeat || g.dpr !== axis.dpr || g.width !== slot.width || g.widthD !== d.backingW) {
      return null;
    }
    if (g.live) return g.axisVersion === axis.version ? 0 : null;
    const topCss = g.heightCss - axis.heightPx - (axis.bottomBeat - g.bottomBeat) * g.pxPerBeat;
    const top = Math.round(topCss * g.dpr);
    if (!(top >= 0 && top + d.backingH <= g.heightD)) return null;
    return top;
  }

  /** The axis a view draws its strips with: the live axis, or one spanning the strip range. */
  function stripAxisFor(slot: Slot, g: StripGeom): TimeAxis {
    if (g.live) return axis;
    const state = {
      pxPerBeat: g.pxPerBeat,
      bottomBeat: g.bottomBeat,
      heightPx: g.heightCss,
      dpr: g.dpr,
      snapDiv: axis.snapDiv,
      beatSnap: axis.beatSnap,
    };
    if (slot.stripAxis === null) slot.stripAxis = createStripAxis(state, context);
    else slot.stripAxis.update(state);
    return slot.stripAxis;
  }

  function sizeStrip(st: StripCanvas, g: StripGeom): CanvasRenderingContext2D | null {
    if (st.canvas.width !== g.widthD) st.canvas.width = g.widthD;
    if (st.canvas.height !== g.heightD) st.canvas.height = g.heightD;
    return st.ctx;
  }

  function backgroundOf(slot: Slot): string {
    return slot.pinned ? theme.gutterBackground : theme.background;
  }

  /** Draws the static layer of the strip range (over the column background). */
  function drawStaticStrip(slot: Slot, d: SlotDom, g: StripGeom): void {
    const ctx = sizeStrip(d.staticStrip, g);
    if (ctx === null) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = backgroundOf(slot);
    ctx.fillRect(0, 0, g.widthD, g.heightD);
    ctx.setTransform(g.dpr, 0, 0, g.dpr, 0, 0);
    drawLayer(slot, 'static', ctx, stripAxisFor(slot, g));
  }

  /** The base strip: a copy of the static strip with the content layer drawn over it. */
  function drawBaseStrip(slot: Slot, d: SlotDom, g: StripGeom): void {
    const ctx = sizeStrip(d.baseStrip, g);
    if (ctx === null) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'copy';
    ctx.drawImage(d.staticStrip.canvas, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.setTransform(g.dpr, 0, 0, g.dpr, 0, 0);
    drawLayer(slot, 'content', ctx, stripAxisFor(slot, g));
  }

  /**
   * The visible canvas: the base strip's rows of the visible range (1:1, or scaled while a zoom gesture runs), then
   * the view's fixed parts, its overlay and the host's overlay (time range, marquee, playhead), all with the live axis.
   */
  function compose(slot: Slot, d: SlotDom, g: StripGeom | null, top: number | null): void {
    const ctx = d.surfaceCtx;
    if (ctx === null) return;
    const wD = d.backingW;
    const hD = d.backingH;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    if (g !== null && top !== null) {
      ctx.globalCompositeOperation = 'copy';
      ctx.drawImage(d.baseStrip.canvas, 0, top, wD, hD, 0, 0, wD, hD);
      ctx.globalCompositeOperation = 'source-over';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = backgroundOf(slot);
      ctx.fillRect(0, 0, wD, hD);
      if (g !== null) {
        // Zoom preview: the strip rows of the visible beat range, stretched to the height (redrawn once it settles).
        const rowOf = (beat: number): number => (g.heightCss - (beat - g.bottomBeat) * g.pxPerBeat) * g.dpr;
        const srcTop = rowOf(axis.topBeat);
        const srcBottom = rowOf(axis.bottomBeat);
        const c0 = Math.max(0, srcTop);
        const c1 = Math.min(g.heightD, srcBottom);
        if (c1 > c0 && srcBottom > srcTop) {
          const k = hD / (srcBottom - srcTop);
          const y0 = (c0 - srcTop) * k;
          ctx.drawImage(d.baseStrip.canvas, 0, c0, wD, c1 - c0, 0, y0, wD, (c1 - c0) * k);
        }
      }
    }
    const dpr = axis.dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawFixed(slot, ctx);
    drawLayer(slot, 'overlay', ctx, axis);
    drawHostOverlay(ctx, slot);
  }

  /**
   * One frame of a column: redraws the strips when they are dirty or no longer cover the visible range (keeping them
   * for a scaled preview while a zoom gesture runs), then composes the visible canvas when anything changed.
   */
  function renderSlot(slot: Slot, now: number): void {
    const d = slot.dom;
    if (d === null || slot.width <= 0 || axis.heightPx <= 0 || d.backingW <= 0 || d.backingH <= 0) {
      slot.dirty.clear();
      return;
    }
    const dirtyStatic = slot.dirty.has('static');
    const dirtyContent = slot.dirty.has('content');
    const dirtyOverlay = slot.dirty.has('overlay');
    slot.dirty.clear();
    slot.contentStreak = dirtyContent ? slot.contentStreak + 1 : 0;
    let g = slot.strip;
    let top = g === null ? null : stripOffset(slot, d, g);
    const zoomPreview =
      top === null &&
      g !== null &&
      !g.live &&
      now < zoomUntil &&
      !dirtyStatic &&
      !dirtyContent &&
      g.dpr === axis.dpr &&
      g.width === slot.width &&
      g.widthD === d.backingW;
    if (g === null || (top === null && !zoomPreview) || dirtyStatic) {
      g = newStripGeom(slot, d, now);
      slot.strip = g;
      drawStaticStrip(slot, d, g);
      drawBaseStrip(slot, d, g);
      top = stripOffset(slot, d, g);
    } else if (dirtyContent) {
      drawBaseStrip(slot, d, g);
    } else if (!dirtyOverlay && slot.composedAxis === axis.version) {
      return;
    }
    slot.composedAxis = axis.version;
    compose(slot, d, g, top);
  }

  function drawHostOverlay(ctx: CanvasRenderingContext2D, slot: Slot): void {
    const w = slot.width;
    const h = axis.heightPx;
    if (!slot.pinned) {
      const ovLoop = overview.liveLoop;
      const live = gutterView.liveRange ?? (ovLoop === null ? null : { range: ovLoop, loop: true });
      const band = live ?? (timeRange === null ? null : { range: timeRange, loop: false });
      if (band !== null) {
        const [s, e] = timeRangeF64(band.range);
        const yS = axis.yAtBeat(s);
        const yE = axis.yAtBeat(e);
        if (yS >= 0 && yE <= h) {
          ctx.save();
          ctx.globalAlpha = 0.5;
          ctx.fillStyle = band.loop ? theme.loopFill : theme.selection;
          ctx.fillRect(0, yE, w, yS - yE);
          ctx.restore();
          ctx.fillStyle = band.loop ? theme.loop : theme.accent;
          ctx.fillRect(0, Math.round(yS), w, 1);
          ctx.fillRect(0, Math.round(yE), w, 1);
        }
      }
      if (press?.kind === 'marquee' && press.moved) {
        const r = marqueeRect(press);
        const x0 = Math.max(0, r.x0 - slot.x);
        const x1 = Math.min(w, r.x1 - slot.x);
        if (x1 >= x0 && r.x1 >= slot.x && r.x0 <= slot.x + w) {
          const yTop = axis.yAtBeat(r.beat1);
          const yBottom = axis.yAtBeat(r.beat0);
          ctx.fillStyle = theme.selection;
          ctx.fillRect(x0, yTop, Math.max(1, x1 - x0), yBottom - yTop);
          ctx.strokeStyle = theme.accent;
          ctx.lineWidth = 1;
          ctx.strokeRect(x0 + 0.5, yTop + 0.5, Math.max(0, x1 - x0 - 1), Math.max(0, yBottom - yTop - 1));
        }
      }
    }
    const y = axis.yAtBeat(playhead);
    if (y >= -1 && y <= h + 1) {
      ctx.fillStyle = theme.playhead;
      ctx.fillRect(0, Math.round(y) - 1, w, 2);
    }
  }

  /**
   * The overview strip: its base (densities, decorations) is drawn into a hidden canvas only when it changes
   * (`overview.baseVersion`, size, theme); every redraw copies it and draws the live part (view box, playhead) on top.
   */
  function drawOverview(): void {
    overviewDirty = false;
    if (overviewCtx === null || overviewCanvas === null || !options.overview) return;
    const dpr = axis.dpr;
    const w = overviewCanvas.width;
    const h = overviewCanvas.height;
    const base = overviewBase;
    const key = `${String(overview.baseVersion)}:${String(w)}x${String(h)}`;
    if (base !== null && (overviewBaseKey !== key || overviewBaseTheme !== theme)) {
      if (base.canvas.width !== w) base.canvas.width = w;
      if (base.canvas.height !== h) base.canvas.height = h;
      const bctx = base.ctx;
      if (bctx !== null) {
        bctx.setTransform(1, 0, 0, 1, 0, 0);
        bctx.fillStyle = theme.overviewBackground;
        bctx.fillRect(0, 0, w, h);
        bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        bctx.save();
        try {
          overview.drawBase(bctx, OVERVIEW_WIDTH, rootH);
        } catch (err) {
          console.error('timeline host: overview failed to draw', err);
        } finally {
          bctx.restore();
        }
      }
      overviewBaseKey = key;
      overviewBaseTheme = theme;
    }
    overviewCtx.setTransform(1, 0, 0, 1, 0, 0);
    overviewCtx.globalCompositeOperation = 'copy';
    if (base !== null) overviewCtx.drawImage(base.canvas, 0, 0);
    overviewCtx.globalCompositeOperation = 'source-over';
    overviewCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    overviewCtx.save();
    try {
      if (base === null) overview.drawBase(overviewCtx, OVERVIEW_WIDTH, rootH);
      overview.drawLive(overviewCtx, OVERVIEW_WIDTH, rootH);
    } catch (err) {
      console.error('timeline host: overview failed to draw', err);
    } finally {
      overviewCtx.restore();
    }
  }

  function frame(now: number, dtMs: number): void {
    if (root === null || disposed) return;
    lastNow = now;
    if (needsMeasure) measure();
    const dpr = currentDpr();
    if (dpr !== axis.dpr) {
      axis.update({ dpr });
      placeSlots();
    }
    if (anim !== null) stepAnimation(now);
    applyFollow();
    if (press !== null) autoscroll(now, dtMs);
    if (revisionSource !== null) {
      let r: number | null = null;
      try {
        r = revisionSource();
      } catch (err) {
        console.error('timeline host: revision source failed', err);
      }
      if (r !== null && r !== lastRevision) {
        if (lastRevision !== null) {
          const p = pendingRevision;
          pendingRevision = null;
          markDocContent(p === null || p === 'all' ? null : p);
        }
        lastRevision = r;
      }
    }
    const axisChanged = axis.version !== drawnAxisVersion;
    if (axisChanged) {
      drawnAxisVersion = axis.version;
      overviewSoftDirty = true;
    }
    // Scroll direction (any source: wheel, follow, reveal animation, drag) for the margins of new strips.
    if (axis.pxPerBeat === lastFramePpb && axis.bottomBeat !== lastFrameBottom && Number.isFinite(lastFrameBottom)) {
      scrollDir = Math.sign(axis.bottomBeat - lastFrameBottom);
      scrollAt = now;
    }
    lastFrameBottom = axis.bottomBeat;
    lastFramePpb = axis.pxPerBeat;
    if (zoomPending) {
      zoomPending = false;
      zoomUntil = now + ZOOM_SETTLE_MS;
    }
    for (const s of slots) {
      if (s.width !== s.laidOutWidth || axisChanged || s.laidOutAxis !== axis.version) {
        s.laidOutWidth = s.width;
        s.laidOutAxis = axis.version;
        try {
          s.view.layout(s.width);
        } catch (err) {
          console.error(`timeline host: column '${s.inst.id}' failed to lay out`, err);
        }
      }
    }
    for (const s of slots) renderSlot(s, now);
    // The overview's live part (view box, playhead) follows the moving view; while it moves every frame (playback
    // follow, a wheel scroll or zoom, UX-030) it redraws at most this often instead of on every frame. A discrete
    // change while still (seek, setView) keeps the immediate redraw through `overviewDirty` directly.
    const viewMoving = playing || now - scrollAt <= SCROLL_RECENT_MS || now < zoomUntil;
    if (overviewSoftDirty && (!viewMoving || now - overviewDrawnAt >= OVERVIEW_PLAYING_MS)) overviewDirty = true;
    if (overviewDirty) {
      overviewSoftDirty = false;
      overviewDrawnAt = now;
      drawOverview();
    }
    if (press?.kind === 'marquee' && marqueeDirty && press.moved) {
      marqueeDirty = false;
      emitMarquee(press, 'update');
    }
    if (rangeSel?.dirty === true) {
      rangeSel.dirty = false;
      emitRangeMarquee(rangeSel.range, rangeSel.mode, 'update');
    }
    flushViewEmit(now, false);
    if (anim !== null || autoscrollAt !== null || viewEmit !== null || now < zoomUntil || overviewSoftDirty) {
      requestFrame();
    }
  }

  function stepAnimation(now: number): void {
    const a = anim;
    if (a === null) return;
    if (a.t0 === null) a.t0 = now;
    const p = Math.min(1, (now - a.t0) / REVEAL_MS);
    const e = 1 - Math.pow(1 - p, 3);
    axis.update({ bottomBeat: clampBottomBeat(a.from + (a.to - a.from) * e, axis.heightPx, axis.pxPerBeat) });
    if (p >= 1) {
      anim = null;
      queueViewEmit(false);
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // Pointer routing
  // -------------------------------------------------------------------------------------------------------------

  function locate(clientX: number, clientY: number): Region {
    if (root === null) return { kind: 'none' };
    const rect = root.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    if (options.overview && px >= rootW - OVERVIEW_WIDTH && px <= rootW) return { kind: 'overview', y: py };
    if (py < headerPx) return { kind: 'header' };
    const y = py - headerPx;
    const pw = pinnedWidth();
    // Below the canvases: the horizontal scrollbar of the columns area (its native drag must keep working).
    if (y > axis.heightPx) return { kind: 'gap', y, scrollbar: scrollbarPx > 0 && px >= pw };
    for (const s of slots) {
      if (s.pinned && px >= s.x && px < s.x + s.width) return { kind: 'column', slot: s, x: px - s.x, y, contentX: px };
    }
    const contentX = px - pw + (scroller?.scrollLeft ?? 0);
    for (const s of slots) {
      if (!s.pinned && contentX >= s.x && contentX < s.x + s.width) {
        return { kind: 'column', slot: s, x: contentX - s.x, y, contentX };
      }
    }
    // Empty space right of the last column.
    return px >= pw ? { kind: 'gap', y, scrollbar: false } : { kind: 'none' };
  }

  /** Column-local coordinates of a client point relative to a given slot (also outside it). */
  function localTo(slot: Slot, clientX: number, clientY: number): { x: number; y: number; contentX: number } {
    const rect = root?.getBoundingClientRect();
    const px = clientX - (rect?.left ?? 0);
    const py = clientY - (rect?.top ?? 0) - headerPx;
    if (slot.pinned) return { x: px - slot.x, y: py, contentX: px };
    const contentX = px - pinnedWidth() + (scroller?.scrollLeft ?? 0);
    return { x: contentX - slot.x, y: py, contentX };
  }

  function toEvent(x: number, y: number, s: PointerSnapshot): TimelinePointerEvent {
    return {
      x,
      y,
      beat: axis.beatAtY(y),
      buttons: s.buttons,
      shift: s.shift,
      ctrl: s.ctrl,
      alt: s.alt,
      detail: s.detail,
    };
  }

  function safeHit(slot: Slot, x: number, y: number): TimelineHit | null {
    try {
      return slot.view.hitTest(x, y, axis);
    } catch (err) {
      console.error(`timeline host: column '${slot.inst.id}' hitTest failed`, err);
      return null;
    }
  }

  function call(slot: Slot, what: string, fn: () => void): void {
    tryCall(slot, what, fn, undefined);
  }

  function tryCall<T>(slot: Slot, what: string, fn: () => T, fallback: T): T {
    try {
      return fn();
    } catch (err) {
      console.error(`timeline host: column '${slot.inst.id}' ${what} failed`, err);
      return fallback;
    }
  }

  function addGestureKeys(): void {
    if (gestureKeyListener !== null || typeof window === 'undefined') return;
    gestureKeyListener = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || press === null) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      cancelGesture();
    };
    window.addEventListener('keydown', gestureKeyListener, true);
  }

  function removeGestureKeys(): void {
    if (gestureKeyListener === null) return;
    window.removeEventListener('keydown', gestureKeyListener, true);
    gestureKeyListener = null;
  }

  function capture(pointerId: number): void {
    try {
      root?.setPointerCapture(pointerId);
    } catch {
      // synthetic events have no active pointer
    }
  }

  function release(pointerId: number): void {
    try {
      if (root?.hasPointerCapture(pointerId) === true) root.releasePointerCapture(pointerId);
    } catch {
      // already released
    }
  }

  function setCursor(c: string): void {
    if (root !== null && root.style.cursor !== c) root.style.cursor = c;
  }

  function onPointerDown(e: PointerEvent): void {
    if (press !== null || isEditableTarget(e.target)) return;
    // macOS Ctrl+click is the secondary click: the contextmenu event that follows handles it.
    if (options.mac && e.ctrlKey && e.button === 0) return;
    lastClient = { x: e.clientX, y: e.clientY };
    const loc = locate(e.clientX, e.clientY);
    const snap = snapshot(e, options.mac);
    if (loc.kind === 'overview') {
      if (e.button !== 0) return;
      e.preventDefault();
      const mode = isLoopModifier(snap) ? 'loop' : 'seek';
      press = {
        kind: 'overview',
        pointerId: e.pointerId,
        start: snap,
        mode,
        startBottom: axis.bottomBeat,
        followSuspended,
        moved: false,
        last: snap,
      };
      capture(e.pointerId);
      addGestureKeys();
      overview.pointerDown(loc.y, rootH, mode);
      return;
    }
    // Middle drag pans over a column and over the empty space right of the last one (not on the scrollbar).
    if (e.button === 1 && (loc.kind === 'column' || (loc.kind === 'gap' && !loc.scrollbar))) {
      e.preventDefault();
      press = {
        kind: 'pan',
        pointerId: e.pointerId,
        slot: loc.kind === 'column' ? loc.slot : null,
        start: snap,
        startBottom: axis.bottomBeat,
        moved: false,
        last: snap,
        lastClientX: e.clientX,
      };
      capture(e.pointerId);
      addGestureKeys();
      setCursor('grabbing');
      return;
    }
    // Other presses on empty space and on the scrollbar strip are left to the browser (scrollbar drag).
    if (loc.kind !== 'column') return;
    const slot = loc.slot;
    const hit = safeHit(slot, loc.x, loc.y);
    const view = slot.view;
    const p: Press = {
      kind: 'view',
      pointerId: e.pointerId,
      slot,
      start: snap,
      startBeat: axis.beatAtY(loc.y),
      startContentX: loc.contentX,
      moved: false,
      last: snap,
    };
    press = p;
    capture(e.pointerId);
    addGestureKeys();
    if (view.onPointerDown !== undefined) {
      const te = toEvent(loc.x, loc.y, snap);
      call(slot, 'onPointerDown', () => view.onPointerDown?.(te, hit));
    } else if (e.button === 0 && !slot.pinned && (hit === null || hit.kind === 'empty')) {
      press = { ...p, kind: 'armed' };
    }
  }

  function startMarquee(p: Extract<Press, { kind: 'view' | 'armed' }>, owner: string, ownerNotified: boolean): void {
    const loc = localTo(p.slot, p.last.clientX, p.last.clientY);
    press = {
      kind: 'marquee',
      pointerId: p.pointerId,
      slot: p.slot,
      owner,
      ownerNotified,
      mode: marqueeModeOf(p.start),
      start: p.start,
      anchorBeat: p.startBeat,
      anchorX: p.startContentX,
      moved: p.moved,
      last: p.last,
      curX: loc.contentX,
      curY: loc.y,
    };
    marqueeDirty = true;
    markAll(['overlay']);
  }

  function handleMove(s: PointerSnapshot): void {
    const p = press;
    if (p === null) return;
    const dist = Math.hypot(s.clientX - p.start.clientX, s.clientY - p.start.clientY);
    p.last = s;
    switch (p.kind) {
      case 'view': {
        if (dist >= DRAG_THRESHOLD_PX) p.moved = true;
        const loc = localTo(p.slot, s.clientX, s.clientY);
        const view = p.slot.view;
        if (view.onPointerMove !== undefined) {
          const te = toEvent(loc.x, loc.y, s);
          call(p.slot, 'onPointerMove', () => view.onPointerMove?.(te));
        }
        return;
      }
      case 'armed': {
        if (dist < DRAG_THRESHOLD_PX) return;
        p.moved = true;
        startMarquee(p, p.slot.inst.id, false);
        return;
      }
      case 'marquee': {
        if (dist >= DRAG_THRESHOLD_PX) p.moved = true;
        const loc = localTo(p.slot, s.clientX, s.clientY);
        p.curX = loc.contentX;
        p.curY = loc.y;
        marqueeDirty = true;
        markAll(['overlay']);
        return;
      }
      case 'pan': {
        if (dist >= 1) p.moved = true;
        const dy = s.clientY - p.start.clientY;
        suspendFollow();
        anim = null;
        setBottom(p.startBottom + dy / axis.pxPerBeat, 'user');
        const dx = s.clientX - p.lastClientX;
        p.lastClientX = s.clientX;
        // Grab: the content follows the pointer, so the view moves the other way.
        if (dx !== 0) horizontalPan(p.slot, -dx, p.slot === null ? 0 : localTo(p.slot, s.clientX, s.clientY).x);
        return;
      }
      case 'overview': {
        p.moved = true;
        if (root !== null) overview.pointerMove(s.clientY - root.getBoundingClientRect().top, rootH);
        return;
      }
    }
  }

  function onPointerMove(e: PointerEvent): void {
    lastClient = { x: e.clientX, y: e.clientY };
    const snap = snapshot(e, options.mac);
    const p = press;
    if (p !== null) {
      if (e.pointerId !== p.pointerId) return;
      handleMove(snap);
      // Let the frame loop check the edges for auto-scroll.
      if (p.moved) requestFrame();
      return;
    }
    if (isEditableTarget(e.target)) {
      leaveHover();
      return;
    }
    const loc = locate(e.clientX, e.clientY);
    if (loc.kind !== 'column') {
      leaveHover();
      setCursor(loc.kind === 'overview' ? 'pointer' : '');
      return;
    }
    const slot = loc.slot;
    if (hoverSlot !== slot) leaveHover();
    hoverSlot = slot;
    hover = { instanceId: slot.inst.id, x: loc.x, y: loc.y, beat: axis.beatAtY(loc.y) };
    const hit = safeHit(slot, loc.x, loc.y);
    setCursor(hit?.cursor ?? 'default');
    const view = slot.view;
    if (view.onPointerMove !== undefined) {
      const te = toEvent(loc.x, loc.y, snap);
      call(slot, 'onPointerMove', () => view.onPointerMove?.(te));
    }
  }

  function leaveHover(): void {
    const s = hoverSlot;
    hoverSlot = null;
    hover = null;
    if (s?.view.onPointerLeave !== undefined) call(s, 'onPointerLeave', () => s.view.onPointerLeave?.());
  }

  function onPointerLeaveRoot(): void {
    if (press === null) {
      leaveHover();
      setCursor('');
    }
  }

  function finishPress(): void {
    const p = press;
    press = null;
    autoscrollAt = null;
    autoscrollXAcc = 0;
    removeGestureKeys();
    if (p !== null) release(p.pointerId);
  }

  function onPointerUp(e: PointerEvent): void {
    const p = press;
    if (p?.pointerId !== e.pointerId) return;
    lastClient = { x: e.clientX, y: e.clientY };
    const s = snapshot(e, options.mac);
    p.last = s;
    finishPress();
    switch (p.kind) {
      case 'view': {
        const loc = localTo(p.slot, s.clientX, s.clientY);
        const view = p.slot.view;
        if (view.onPointerUp !== undefined) {
          const te = toEvent(loc.x, loc.y, s);
          call(p.slot, 'onPointerUp', () => view.onPointerUp?.(te));
        }
        break;
      }
      case 'armed': {
        // A click on empty space of a column without handlers: an empty replace-marquee (clears the selection).
        emit('marquee', {
          phase: 'end',
          owner: p.slot.inst.id,
          mode: marqueeModeOf(p.start),
          beat0: p.startBeat,
          beat1: p.startBeat,
          items: { notes: [], keys: [], segments: [] },
          columns: [],
        });
        break;
      }
      case 'marquee': {
        const loc = localTo(p.slot, s.clientX, s.clientY);
        p.curX = loc.contentX;
        p.curY = loc.y;
        emitMarquee(p, 'end');
        if (p.ownerNotified) {
          const owner = slotById.get(p.owner);
          const view = owner?.view;
          if (owner !== undefined && view?.onPointerUp !== undefined) {
            const ol = localTo(owner, s.clientX, s.clientY);
            const te = toEvent(ol.x, ol.y, s);
            call(owner, 'onPointerUp', () => view.onPointerUp?.(te));
          }
        }
        markAll(['overlay']);
        break;
      }
      case 'pan':
        setCursor('');
        break;
      case 'overview':
        if (root !== null) overview.pointerUp(s.clientY - root.getBoundingClientRect().top, rootH);
        break;
    }
  }

  function cancelGesture(): void {
    const p = press;
    if (p === null) return;
    finishPress();
    switch (p.kind) {
      case 'view':
      case 'armed': {
        const view = p.slot.view;
        if (view.onPointerCancel !== undefined) call(p.slot, 'onPointerCancel', () => view.onPointerCancel?.());
        break;
      }
      case 'marquee': {
        emitMarquee(p, 'cancel');
        const owner = p.ownerNotified ? slotById.get(p.owner) : undefined;
        if (owner?.view.onPointerCancel !== undefined)
          call(owner, 'onPointerCancel', () => owner.view.onPointerCancel?.());
        markAll(['overlay']);
        break;
      }
      case 'pan':
        setBottom(p.startBottom, 'user');
        setCursor('');
        break;
      case 'overview':
        // A seek drag reports 'end' at the playhead of the press, a loop drag 'cancel'.
        overview.cancel();
        if (p.mode === 'seek') restoreAfterOverviewSeek(p);
        break;
    }
  }

  function cancelPressFor(slot: Slot): void {
    if (press !== null && 'slot' in press && press.slot === slot) cancelGesture();
  }

  function onPointerCancelEvent(e: PointerEvent): void {
    if (press?.pointerId === e.pointerId) cancelGesture();
  }

  function onLostCapture(e: PointerEvent): void {
    // Capture lost while still pressed (element removed, another capture): treat as cancel.
    if (press?.pointerId === e.pointerId && e.buttons !== 0) cancelGesture();
  }

  function onDoubleClick(e: MouseEvent): void {
    if (isEditableTarget(e.target)) return;
    const loc = locate(e.clientX, e.clientY);
    if (loc.kind !== 'column') return;
    const view = loc.slot.view;
    if (view.onDoubleClick === undefined) return;
    const hit = safeHit(loc.slot, loc.x, loc.y);
    const te = toEvent(loc.x, loc.y, snapshot(e, options.mac));
    call(loc.slot, 'onDoubleClick', () => view.onDoubleClick?.(te, hit));
  }

  function onContextMenu(e: MouseEvent): void {
    if (isEditableTarget(e.target)) return;
    const loc = locate(e.clientX, e.clientY);
    if (loc.kind === 'header') return;
    e.preventDefault();
    if (loc.kind !== 'column') return;
    lastClient = { x: e.clientX, y: e.clientY };
    const view = loc.slot.view;
    if (view.onContextMenu === undefined) return;
    const hit = safeHit(loc.slot, loc.x, loc.y);
    const te = toEvent(loc.x, loc.y, snapshot(e, options.mac));
    call(loc.slot, 'onContextMenu', () => view.onContextMenu?.(te, hit));
  }

  /**
   * Edge auto-scroll while dragging (called every frame of a press): near the top / bottom edge time scrolls; while a
   * marquee is live and the columns overflow, near the left / right edge of the columns area the area also scrolls
   * sideways, so the band can reach columns off screen without letting go. The speed grows with the depth into the
   * edge zone (doubled once the pointer is outside), and the gesture gets a move at the new position.
   */
  function autoscroll(now: number, dtMs: number): void {
    const p = press;
    if (p === null || !p.moved || p.kind === 'pan' || p.kind === 'overview' || root === null) {
      autoscrollAt = null;
      autoscrollXAcc = 0;
      return;
    }
    const rect = root.getBoundingClientRect();
    const y = p.last.clientY - rect.top - headerPx;
    const h = axis.heightPx;
    let dir = 0;
    let depth = 0;
    if (y < AUTOSCROLL_EDGE_PX) {
      dir = 1;
      depth = (AUTOSCROLL_EDGE_PX - y) / AUTOSCROLL_EDGE_PX;
    } else if (y > h - AUTOSCROLL_EDGE_PX) {
      dir = -1;
      depth = (y - (h - AUTOSCROLL_EDGE_PX)) / AUTOSCROLL_EDGE_PX;
    }
    const [dirX, depthX] = p.kind === 'marquee' && !p.slot.pinned ? sidewaysEdge(p.last.clientX - rect.left) : [0, 0];
    if (dir === 0 && dirX === 0) {
      autoscrollAt = null;
      autoscrollXAcc = 0;
      return;
    }
    const dt = autoscrollAt === null ? Math.min(dtMs, 16) : Math.min(64, Math.max(0, now - autoscrollAt));
    autoscrollAt = now;
    let moved = false;
    const px = Math.min(2, depth) * AUTOSCROLL_PX_PER_MS * dt;
    if (dir !== 0 && px > 0) {
      scrollBy((dir * px) / axis.pxPerBeat);
      moved = true;
    }
    if (dirX === 0) autoscrollXAcc = 0;
    else {
      autoscrollXAcc += dirX * Math.min(2, depthX) * AUTOSCROLL_PX_PER_MS * dt;
      const whole = Math.trunc(autoscrollXAcc);
      if (whole !== 0) {
        autoscrollXAcc -= whole;
        scrollColumnsBy(whole);
        moved = true;
      }
    }
    if (moved) handleMove(p.last);
  }

  /**
   * Sideways auto-scroll direction (-1 left, 1 right, 0 none) and depth for a pointer at root x `px`: inside
   * AUTOSCROLL_EDGE_PX of the left or right edge of the visible columns area (or beyond it), and only while the
   * columns area can still scroll that way.
   */
  function sidewaysEdge(px: number): readonly [number, number] {
    if (scroller === null) return [0, 0];
    const left = pinnedWidth();
    const right = rootW - overviewW();
    const maxScroll = Math.max(0, contentWidth() - (right - left));
    if (maxScroll <= 0 || right - left <= 2 * AUTOSCROLL_EDGE_PX) return [0, 0];
    const scrollLeft = scroller.scrollLeft;
    if (px < left + AUTOSCROLL_EDGE_PX && scrollLeft > 0) {
      return [-1, (left + AUTOSCROLL_EDGE_PX - px) / AUTOSCROLL_EDGE_PX];
    }
    if (px > right - AUTOSCROLL_EDGE_PX && scrollLeft < maxScroll - 0.5) {
      return [1, (px - (right - AUTOSCROLL_EDGE_PX)) / AUTOSCROLL_EDGE_PX];
    }
    return [0, 0];
  }

  // -------------------------------------------------------------------------------------------------------------
  // Marquee
  // -------------------------------------------------------------------------------------------------------------

  function marqueeRect(p: Extract<Press, { kind: 'marquee' }>): {
    x0: number;
    x1: number;
    beat0: number;
    beat1: number;
  } {
    const curBeat = axis.beatAtY(p.curY);
    return {
      x0: Math.min(p.anchorX, p.curX),
      x1: Math.max(p.anchorX, p.curX),
      beat0: Math.min(p.anchorBeat, curBeat),
      beat1: Math.max(p.anchorBeat, curBeat),
    };
  }

  function queryRect(beat0: number, beat1: number, x0: number, x1: number): MarqueeItems & { columns: string[] } {
    const notes: NoteId[] = [];
    const keys: KeyId[] = [];
    const segments: KeyId[] = [];
    const seenN = new Set<NoteId>();
    const seenK = new Set<KeyId>();
    const seenS = new Set<KeyId>();
    const columns: string[] = [];
    const lo = Math.min(x0, x1);
    const hi = Math.max(x0, x1);
    const b0 = Math.min(beat0, beat1);
    const b1 = Math.max(beat0, beat1);
    for (const s of slots) {
      if (s.pinned || s.view.itemsInRect === undefined) continue;
      if (hi < s.x || lo >= s.x + s.width) continue;
      // An open side (infinity) is passed on unchanged: the column applies no X limit there.
      const lx0 = lo === Number.NEGATIVE_INFINITY ? lo : Math.max(0, lo - s.x);
      const lx1 = hi === Number.POSITIVE_INFINITY ? hi : Math.min(s.width, hi - s.x);
      let res: ReturnType<NonNullable<TimelineColumnView['itemsInRect']>> | null = null;
      try {
        res = s.view.itemsInRect(b0, b1, lx0, lx1, axis);
      } catch (err) {
        console.error(`timeline host: column '${s.inst.id}' itemsInRect failed`, err);
      }
      columns.push(s.inst.id);
      if (res === null) continue;
      for (const n of res.notes ?? []) {
        if (seenN.has(n)) continue;
        seenN.add(n);
        notes.push(n);
      }
      for (const k of res.keys ?? []) {
        if (seenK.has(k)) continue;
        seenK.add(k);
        keys.push(k);
      }
      for (const k of res.segments ?? []) {
        if (seenS.has(k)) continue;
        seenS.add(k);
        segments.push(k);
      }
    }
    return { notes, keys, segments, columns };
  }

  function emitMarquee(p: Extract<Press, { kind: 'marquee' }>, phase: MarqueeEvent['phase']): void {
    const r = marqueeRect(p);
    const q =
      phase === 'cancel' ? { notes: [], keys: [], segments: [], columns: [] } : queryRect(r.beat0, r.beat1, r.x0, r.x1);
    emit('marquee', {
      phase,
      owner: p.owner,
      mode: p.mode,
      beat0: r.beat0,
      beat1: r.beat1,
      items: { notes: q.notes, keys: q.keys, segments: q.segments },
      columns: q.columns,
    });
  }

  // -------------------------------------------------------------------------------------------------------------
  // Wheel
  // -------------------------------------------------------------------------------------------------------------

  /** Whole steps for a wheel delta: a discrete notch is at least one step, trackpad deltas accumulate. */
  function wheelSteps(dy: number, now: number): number {
    if (now - wheelAccAt > 300) wheelAcc = 0;
    wheelAccAt = now;
    if (Math.abs(dy) >= 20) {
      wheelAcc = 0;
      return Math.sign(dy) * Math.max(1, Math.round(Math.abs(dy) / WHEEL_NOTCH_PX));
    }
    wheelAcc += dy;
    const steps = Math.trunc(wheelAcc / WHEEL_NOTCH_PX);
    wheelAcc -= steps * WHEEL_NOTCH_PX;
    return steps;
  }

  function horizontalZoom(slot: Slot, factor: number, x: number): void {
    const view = slot.view;
    if (view.zoomX !== undefined) call(slot, 'zoomX', () => view.zoomX?.(factor, x));
    else {
      emit('horizontal', {
        kind: 'zoomX',
        instanceId: slot.inst.id,
        factor,
        dxPx: 0,
        x,
        consume: () => undefined,
      });
    }
  }

  /** Scrolls the columns area sideways (positive = towards the later columns; the browser clamps). */
  function scrollColumnsBy(dx: number): void {
    if (scroller === null || dx === 0) return;
    scroller.scrollLeft += dx;
  }

  /**
   * Horizontal pan (panX convention: positive moves the view right). A scrolling column with `panX` pans its own
   * axis; otherwise a 'horizontal' listener may consume it, and an unconsumed pan scrolls the columns area.
   */
  function horizontalPan(slot: Slot | null, dx: number, x: number): void {
    if (dx === 0) return;
    if (slot !== null && !slot.pinned) {
      const view = slot.view;
      if (view.panX !== undefined) {
        call(slot, 'panX', () => view.panX?.(dx));
        return;
      }
      const claim = { consumed: false };
      emit('horizontal', {
        kind: 'panX',
        instanceId: slot.inst.id,
        factor: 1,
        dxPx: dx,
        x,
        consume: () => {
          claim.consumed = true;
        },
      });
      if (claim.consumed) return;
    }
    scrollColumnsBy(dx);
  }

  function onWheel(e: WheelEvent): void {
    if (isEditableTarget(e.target)) return;
    const loc = locate(e.clientX, e.clientY);
    if (loc.kind === 'header' || loc.kind === 'none') return;
    const slot = loc.kind === 'column' ? loc.slot : null;
    if (slot?.view.onWheel !== undefined) {
      const v = slot.view;
      if (tryCall(slot, 'onWheel', () => v.onWheel?.(e, axis) === true, false)) {
        e.preventDefault();
        return;
      }
    }
    e.preventDefault();
    const { dx, dy } = normalizeWheel(e, axis.heightPx);
    const now = clock();
    const mac = options.mac;
    const ctrl = e.ctrlKey || (mac && e.metaKey);
    // Empty space right of the last column scrolls and zooms like a column (the scrollbar strip: at the bottom).
    const anchorY =
      loc.kind === 'column' || loc.kind === 'gap' ? Math.min(axis.heightPx, Math.max(0, loc.y)) : axis.heightPx / 2;
    const x = loc.kind === 'column' ? loc.x : 0;
    if (ctrl && e.shiftKey) {
      if (slot !== null && !slot.pinned) horizontalZoom(slot, wheelZoomFactor(dy !== 0 ? dy : dx), x);
      return;
    }
    if (ctrl) {
      const pinch = e.ctrlKey && e.deltaMode === 0 && !Number.isInteger(e.deltaY);
      if (options.wheelPreset === 'rpe' && !pinch) {
        const steps = wheelSteps(dy, now);
        if (steps !== 0)
          emit('wheel', { kind: 'ctrl', steps, instanceId: slot?.inst.id ?? null, beat: axis.beatAtY(anchorY) });
        return;
      }
      zoomAtY(wheelZoomFactor(dy), anchorY, true);
      return;
    }
    if (e.altKey) {
      if (options.wheelPreset === 'rpe') {
        scrollBy(scrollBeatsForDelta(dy !== 0 ? dy : dx, options.scrollSensitivity, true));
        return;
      }
      if (!options.altFree) {
        const steps = wheelSteps(dy !== 0 ? dy : dx, now);
        if (steps !== 0) {
          // Wheel up (negative delta) = finer grid.
          const div = cycleSnapDiv(axis.snapDiv, -steps);
          if (div !== axis.snapDiv) {
            axis.update({ snapDiv: div });
            markAll(['static']);
            queueViewEmit(false);
          }
        }
        return;
      }
    }
    if (loc.kind === 'gap' && loc.scrollbar) {
      // Over the horizontal scrollbar any wheel scrolls the columns area sideways.
      scrollColumnsBy(Math.abs(dx) > Math.abs(dy) ? dx : dy);
      return;
    }
    let vertical = dy;
    let horizontal = dx;
    const fast = e.shiftKey && !mac;
    if (fast && vertical === 0) {
      // Windows/Linux browsers turn Shift+wheel into deltaX: keep it vertical (fast scroll).
      vertical = horizontal;
      horizontal = 0;
    }
    if (vertical !== 0) scrollBy(scrollBeatsForDelta(vertical, options.scrollSensitivity, fast));
    if (horizontal !== 0 && loc.kind !== 'overview') horizontalPan(slot, horizontal, x);
  }

  // -------------------------------------------------------------------------------------------------------------
  // Theme
  // -------------------------------------------------------------------------------------------------------------

  function refreshTheme(): void {
    theme = readTimelineTheme(root ?? container);
    markAll();
    overviewDirty = true;
    requestFrame();
  }

  /** Re-arms a one-shot media query for the current pixel ratio (browser zoom, moving to another screen). */
  function watchDpr(): void {
    if (typeof matchMedia !== 'function' || disposed) return;
    let mq: MediaQueryList;
    try {
      mq = matchMedia(`(resolution: ${String(currentDpr())}dppx)`);
    } catch {
      return;
    }
    const fn = (): void => {
      mq.removeEventListener('change', fn);
      needsMeasure = true;
      requestFrame();
      watchDpr();
    };
    mq.addEventListener('change', fn);
    domCleanups.push(() => {
      mq.removeEventListener('change', fn);
    });
  }

  function observeTheme(): void {
    if (themeObserver !== null || typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;
    themeObserver = new MutationObserver(() => {
      refreshTheme();
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class', 'style'],
    });
    if (typeof matchMedia === 'function') {
      for (const q of ['(prefers-color-scheme: dark)', '(prefers-contrast: more)']) {
        try {
          const mq = matchMedia(q);
          const fn = (): void => {
            refreshTheme();
          };
          mq.addEventListener('change', fn);
          domCleanups.push(() => {
            mq.removeEventListener('change', fn);
          });
        } catch {
          // matchMedia without listeners (old engines): theme switches still arrive through data-theme
        }
      }
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------------------------------------------

  const api: TimelineHostController = {
    axis,
    get options() {
      return options;
    },
    get playhead() {
      return playhead;
    },
    get playing() {
      return playing;
    },
    get following() {
      return isFollowing();
    },
    get waveform() {
      return waveformSource;
    },
    get theme() {
      return theme;
    },
    get meter() {
      return meterMap;
    },
    get root() {
      return root;
    },

    mount(el) {
      if (disposed) return;
      container = el;
      const r = ensureDom();
      if (r.parentElement !== el) el.appendChild(r);
      theme = readTimelineTheme(r);
      if (themeObserver === null) watchDpr();
      observeTheme();
      if (resizeObserver === null && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          needsMeasure = true;
          requestFrame();
        });
      }
      resizeObserver?.disconnect();
      resizeObserver?.observe(el);
      // The columns area's content box shrinks when a classic scrollbar appears (also from outside the host's own
      // layout, e.g. a scrollbar style change): re-measure then too.
      if (scroller !== null) resizeObserver?.observe(scroller);
      unregisterFrame ??= scheduler.add(frameId, 'timeline', frame);
      needsMeasure = true;
      relayout();
      measure();
      markAll();
    },

    setColumnDefs(next) {
      defs = [...next];
      relayout();
    },

    setDecorations(next) {
      for (const u of providerUnsubs) u();
      providers = [...next];
      providerUnsubs = providers.map((p) =>
        p.subscribe(() => {
          overview.invalidate('decorations');
          overviewDirty = true;
          const g = slotById.get(GUTTER_INSTANCE_ID);
          if (g !== undefined) markSlot(g, ['content', 'overlay']);
          requestFrame();
        }),
      );
      overview.invalidate('decorations');
      overviewDirty = true;
      const g = slotById.get(GUTTER_INSTANCE_ID);
      if (g !== undefined) markSlot(g, ['content']);
      requestFrame();
    },

    setView(cfg) {
      const prev = currentView();
      // Zoom, bottom and snap apply only as new intents (late echoes of the host's own reports are ignored); the
      // other fields (follow, ratio, meter, beat snap, waveform opacity) always apply.
      const asked: AxisFields = {
        pxPerBeat: clampPxPerBeat(cfg.pxPerBeat),
        bottomBeat: cfg.bottomBeat,
        snapDiv: clampDiv(cfg.snapDiv),
      };
      const take = axisFieldsToApply(asked);
      lastSynced = asked;
      // An explicit scroll position supersedes a reveal that waits for the first layout.
      if (take.bottomBeat) pendingView = null;
      const ppb = take.pxPerBeat ? asked.pxPerBeat : axis.pxPerBeat;
      const snapDiv = take.snapDiv ? asked.snapDiv : axis.snapDiv;
      view = { ...cfg, pxPerBeat: ppb, snapDiv, bottomBeat: axis.bottomBeat };
      axis.update({ pxPerBeat: ppb, snapDiv, beatSnap: cfg.beatSnap });
      if (cfg.followPlayhead && !prev.followPlayhead) followSuspended = false;
      if (!(playing && isFollowing())) {
        if (take.bottomBeat) anim = null;
        // Re-clamped even when only the zoom changed (the lower bound depends on the visible span).
        setBottom(take.bottomBeat ? asked.bottomBeat : axis.bottomBeat, 'api');
      }
      view = { ...view, bottomBeat: axis.bottomBeat, snapDiv: axis.snapDiv };
      if (!sameMeter(prev.meter, cfg.meter)) {
        meterMap = compileMeter(cfg.meter);
        markAll(['static', 'content']);
      }
      if (
        prev.snapDiv !== axis.snapDiv ||
        prev.beatSnap !== axis.beatSnap ||
        prev.waveformOpacity !== cfg.waveformOpacity
      ) {
        markAll(['static']);
      }
      if (prev.followPlayhead !== cfg.followPlayhead) emit('follow', { following: isFollowing() });
      applyFollow();
      requestFrame();
    },

    onViewChange(cb) {
      viewListeners.add(cb);
      return () => {
        viewListeners.delete(cb);
      };
    },

    setPlayhead(beat) {
      if (!Number.isFinite(beat) || beat === playhead) return;
      playhead = beat;
      applyFollow();
      markAll(['overlay']);
      overviewSoftDirty = true;
    },

    setPlaying(on) {
      if (on === playing) return;
      playing = on;
      if (on) {
        // Playback (re)starts: a follow paused by a manual scroll resumes.
        anim = null;
        followSuspended = false;
        emit('follow', { following: isFollowing() });
        applyFollow();
      } else {
        flushViewEmit(lastNow, true);
      }
      requestFrame();
    },

    resumeFollow() {
      if (!followSuspended) return;
      followSuspended = false;
      emit('follow', { following: isFollowing() });
      applyFollow();
    },

    invalidate(instanceId, layer) {
      const layers = layer === undefined ? LAYERS : [layer];
      if (instanceId === undefined) {
        for (const s of slots) markSlot(s, layers);
        if (layer === undefined) overviewDirty = true;
      } else {
        const s = slotById.get(instanceId);
        if (s !== undefined) markSlot(s, layers);
      }
      requestFrame();
    },

    applyChangeSet(cs) {
      const timing = cs.dirty.tempo || cs.dirty.timing;
      const global = timing || isGlobalDirty(cs.dirty);
      const change: DocChangeRequest = { dirty: cs.dirty, ops: cs.ops };
      if (global) pendingRevision = 'all';
      else if (pendingRevision === null) pendingRevision = change;
      else if (pendingRevision !== 'all') pendingRevision = mergeChange(pendingRevision, change);
      if (timing) markAll(['static', 'content', 'overlay']);
      else markDocContent(change);
      if (timing) overviewDirty = true;
    },

    setRevisionSource(fn) {
      revisionSource = fn;
      lastRevision = null;
      if (fn !== null) {
        try {
          lastRevision = fn();
        } catch {
          lastRevision = null;
        }
      }
      pendingRevision = null;
      markDocContent(null);
    },

    relayout,

    requestFrame,

    reveal(beat, where = 'center') {
      if (!Number.isFinite(beat)) return;
      if (axis.heightPx <= 0) {
        // Not laid out yet: applied (without animation) as soon as the timeline has a height.
        pendingView = { kind: 'reveal', beat, where };
        return;
      }
      pendingView = null;
      revealBeat(beat, where, true);
    },

    setPxPerBeat(pxPerBeat, anchorBeat) {
      const ppb = clampPxPerBeat(pxPerBeat);
      if (ppb === axis.pxPerBeat) return;
      const [b0, b1] = axis.range;
      const anchor = anchorBeat ?? (playhead >= b0 && playhead <= b1 ? playhead : (b0 + b1) / 2);
      const anchorY = axis.yAtBeat(anchor);
      anim = null;
      axis.update({ pxPerBeat: ppb });
      setBottom(bottomForAnchor(anchor, anchorY, axis.heightPx, ppb), 'api');
      applyFollow();
      queueViewEmit(false);
    },

    capturePointer(owner) {
      const p = press;
      if (p === null || (p.kind !== 'view' && p.kind !== 'armed') || p.slot.pinned) return;
      startMarquee(p, owner, slotById.has(owner));
    },

    on(type, cb) {
      let set = listeners.get(type);
      if (set === undefined) {
        set = new Set();
        listeners.set(type, set);
      }
      const fn = cb as (e: never) => void;
      set.add(fn);
      return () => {
        set.delete(fn);
      };
    },

    setOptions(p) {
      const prev = options;
      options = { ...options, ...p };
      const hintsChanged =
        prev.translate !== options.translate || prev.altFree !== options.altFree || prev.mac !== options.mac;
      if (hintsChanged && root !== null) {
        root.setAttribute('aria-label', tr({ key: m.region }));
        scroller?.setAttribute('aria-label', tr({ key: m.columns }));
        if (overviewEl !== null) overviewEl.title = hint(m.overviewHint);
        if (prev.translate !== options.translate) relayout();
        const g = slotById.get(GUTTER_INSTANCE_ID);
        if (g?.dom != null) g.dom.el.title = hint(m.gutterHint);
      }
      if (
        prev.waveformColumn !== options.waveformColumn ||
        prev.headerHeight !== options.headerHeight ||
        prev.overview !== options.overview
      ) {
        relayout();
      }
    },

    view: currentView,

    setWaveform(src) {
      const had = waveformSource !== null;
      waveformSource = src;
      if (had !== (src !== null)) relayout();
      // Lanes draw the waveform backdrop on their static layer too.
      markAll(['static']);
    },

    setOverview(data) {
      overviewData = data;
      overview.invalidate('data');
      overviewDirty = true;
      requestFrame();
    },

    getTimeRange: () => timeRange,

    setTimeRange(range) {
      timeRange = range;
      markAll(['overlay']);
    },

    setColumnWidths(widths) {
      userWidths.clear();
      for (const [id, w] of Object.entries(widths)) if (Number.isFinite(w) && w > 0) userWidths.set(id, w);
      layoutColumns();
    },

    columnWidths: () => Object.fromEntries(userWidths),

    getLayout: () => slots.map(layoutOf),

    headerSlot: (id) => slotById.get(id)?.dom?.headerEl ?? null,

    revealColumn(id) {
      const s = slotById.get(id);
      if (s === undefined || s.pinned || scroller === null) return;
      const viewW = Math.max(0, rootW - pinnedWidth() - overviewW());
      const left = scroller.scrollLeft;
      if (s.x < left) scroller.scrollLeft = s.x;
      else if (s.x + s.width > left + viewW) scroller.scrollLeft = s.x + s.width - viewW;
    },

    frameRange(beat0, beat1) {
      if (!Number.isFinite(beat0) || !Number.isFinite(beat1)) return;
      if (axis.heightPx <= 0) {
        pendingView = { kind: 'frame', beat0, beat1 };
        return;
      }
      pendingView = null;
      frameBeats(beat0, beat1);
    },

    queryRect,

    hoverInfo: () => hover,

    cancelGesture,

    refreshTheme,

    dispose() {
      if (disposed) return;
      cancelGesture();
      disposed = true;
      unregisterFrame?.();
      unregisterFrame = null;
      resizeObserver?.disconnect();
      resizeObserver = null;
      themeObserver?.disconnect();
      themeObserver = null;
      for (const c of domCleanups.splice(0)) c();
      for (const u of providerUnsubs) u();
      providerUnsubs = [];
      providers = [];
      for (const s of slots) {
        removeSlotDom(s);
        disposeView(s.view);
      }
      slots = [];
      slotById.clear();
      gutterView.dispose();
      waveformView.dispose();
      root?.remove();
      root = null;
      container = null;
      listeners.clear();
      viewListeners.clear();
    },
  };
  return api;
}

/** The contracted factory (render/types.ts `CreateTimelineHost`); returns the controller with its additive API. */
export const createTimelineHost = createTimelineHostController satisfies CreateTimelineHost;

/** Narrowing helper for code that only holds the contract type. */
export function asTimelineHostController(host: TimelineHostApi): TimelineHostController | null {
  return 'setPlaying' in host && 'on' in host ? (host as TimelineHostController) : null;
}

/** Exact beats of a time range as f64 (re-exported for panels). */
export { timeRangeF64 };
